/**
 * English messages (default locale). Kept in sync with nl.ts — same key
 * tree, translated values. Structured per surface; the landing block
 * mirrors the sections of LandingView.vue.
 */
export default {
  common: {
    login: 'Log in',
    tryFree: 'Try for free',
    languageLabel: 'Language',
    product: 'Product',
    pricing: 'Pricing',
    faq: 'FAQ',
    contact: 'Contact',
    save: 'Save',
    cancel: 'Cancel',
    loading: 'Loading…',
    yes: 'Yes',
    no: 'No',
  },

  toast: {
    regionLabel: 'Notifications',
    dismiss: 'Dismiss notification: {title}',
  },

  modal: {
    close: 'Close',
  },

  rte: {
    toolbar: 'Formatting',
    content: 'Text content',
    heading: 'Heading {level}',
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    bulletList: 'Bullet list',
    orderedList: 'Numbered list',
    link: 'Add or edit link',
    linkShort: 'Link',
    insertTable: 'Insert table',
    insertImage: 'Insert image',
    imageSettings: 'Image settings',
    imageAlt: 'Alt text',
    imageAltHint: 'Describes the image for people who cannot see it. Leave empty if it is purely decorative.',
    tableToolbar: 'Table editing',
    deleteTable: 'Delete table',
    linkPrompt: 'Link URL (leave empty to remove the link):',
    altPrompt: 'Alt text for the image (description for screen readers):',
    uploadFailed: 'Image upload failed',
    textColor: 'Text colour',
    textColorToolbar: 'Text colour',
    fontSize: 'Text size (px)',
    fontSizeDefault: 'auto',
    fontSizeReset: 'Back to the default text size',
    fontSizeHeadingHint: 'Heading sizes come from the theme and cannot be changed here.',
    textColorCustom: 'Custom colour',
    textColorDefault: 'Default colour',
    align: {
      left: 'Align left',
      center: 'Centre',
      right: 'Align right',
    },
    colors: {
      dark: 'Dark',
      gray: 'Grey',
      red: 'Red',
      orange: 'Orange',
      green: 'Green',
      blue: 'Blue',
      purple: 'Purple',
      pink: 'Pink',
    },
    table: {
      rowBefore: '+ Row above',
      rowAfter: '+ Row below',
      rowDelete: '− Row',
      colBefore: '+ Column left',
      colAfter: '+ Column right',
      colDelete: '− Column',
      headerRow: 'Header row',
      headerColumn: 'Header column',
    },
  },

  breadcrumb: {
    label: 'Breadcrumb',
    home: 'Home',
  },

  home: {
    subtitle: 'Document, manage and share your design system with your team',
    features: {
      pages: {
        title: 'Pages',
        description: 'Create and manage documentation pages with a flexible page builder.',
      },
      figma: {
        title: 'Figma integration',
        description: 'Import designs directly from Figma and sync automatically.',
      },
      tokens: {
        title: 'Design tokens',
        description: 'Manage colours, typography and spacing as reusable tokens.',
      },
      code: {
        title: 'Code generation',
        description: 'Automatically generate CSS, React, Vue and more from your designs.',
      },
    },
    a11yHeading: 'Accessibility',
    a11yBody:
      'This platform conforms to the WCAG 2.2 AA guidelines for digital accessibility. Use Tab to navigate and Enter to activate.',
    onboarding: {
      title: 'Getting started',
      progress: '{done} of {total} steps done',
      dismiss: 'Hide checklist',
      optional: '(optional)',
      doneLabel: 'Done',
      allDone: 'All steps done - your documentation is up and running.',
      steps: {
        figma: {
          title: 'Connect with Figma',
          description: 'Sign in to Figma once and Pentrical can read your design files. Documenting without Figma works too.',
        },
        import: {
          title: 'Import your first Figma file',
          description: 'Paste the link of a Figma file; measurements, colours and tokens come along automatically.',
        },
        page: {
          title: 'Create a category and your first page',
          description: 'Categories are the main structure of your documentation; pages are created in the left-hand menu.',
        },
        publish: {
          title: 'Publish and view as visitor',
          description: 'A page only becomes visible to visitors after publishing. "View as visitor" shows you exactly what they see.',
        },
        team: {
          title: 'Invite your team',
          description: 'Invite colleagues who write with you via My account. People who only read need no account.',
        },
      },
    },
  },

  start: {
    title: 'Getting started with Pentrical',
    welcome:
      'Good to have you here. Five steps take your design system documentation online - this screen keeps track of how far you are.',
    structure: {
      title: 'How Pentrical is organised',
      menu: 'The menu at the top holds your categories - the chapters of your documentation.',
      sidebar: 'The left sidebar shows the pages inside the category you are working in.',
      publish: 'A page only becomes visible to visitors once you publish it.',
    },
    help: {
      title: 'Need a hand?',
      body: 'The help center has short guides for every part of Pentrical.',
      link: 'Open the help center',
    },
  },

  pageTabs: {
    nav: 'Page tabs',
    renameLabel: 'New tab name',
    draft: 'Draft',
    actionsFor: 'Actions for tab {title}',
    newTabPlaceholder: 'New tab name…',
    newTabLabel: 'Name of new tab',
    addTab: 'Add tab',
    rename: 'Rename',
    hideFromVisitors: 'Hide from visitors',
    makeVisible: 'Make visible',
    moveLeft: 'Move left',
    moveRight: 'Move right',
    delete: 'Delete',
    noCategoryError: 'This page has no category; tabs are not possible.',
    added: 'Tab "{title}" added',
    addFailed: 'Failed to add tab',
    renameFailed: 'Failed to rename',
    visibilityFailed: 'Failed to change visibility',
    moved: 'Tab "{title}" moved to position {pos} of {total}',
    moveFailed: 'Failed to move',
    confirmDelete: 'Are you sure you want to delete tab "{title}"? This cannot be undone.',
    deleted: 'Tab "{title}" deleted',
    deleteFailed: 'Failed to delete',
  },


  pageEditor: {
    breadcrumbHome: 'Home',
    breadcrumbPages: 'Pages',
    breadcrumbEditor: 'Editor',

    loading: 'Loading page…',
    notFound: 'Page not found',
    backToPages: 'Back to pages',

    cancelBack: 'Cancel and return to the page',
    published: 'Published',
    draft: 'Draft',
    notSaved: '• Not saved',
    preview: 'Preview',
    previewPrompt: {
      title: 'The preview shows the saved version',
      body: 'You have changes that have not been saved yet. They are not in the preview - save first and you will see the page exactly as it is now.',
      publishedNote: 'This page is live, so saving publishes these changes straight away.',
      saveAndPreview: 'Save and preview',
      previewAnyway: 'Preview without saving',
      cancel: 'Cancel',
    },
    backToDraft: 'Back to draft',
    save: 'Save',
    update: 'Update',
    publish: 'Publish',

    title: 'Title',
    titlePlaceholder: 'Page title',
    summary: 'Summary',
    summaryHint: '(optional, shown below the title)',
    summaryPlaceholder: 'Short description of this page',
    tabSummaryNotice: 'The title above is the tab name. On this tab, visitors see the title and summary of the main page —',
    tabSummaryEditHere: 'edit them here',
    tabSummaryEditParent: 'edit them on the main page',

    tabs: 'Tabs',
    tabsHint: '(subpages, shown as a tab bar and in the menu)',

    setAsHomepage: 'Set as homepage',
    setAsHomepageHint: 'Visitors will see this page first on the home page.',
    hideSidebar: 'Hide left menu',
    hideSidebarHint: 'The home page will then show no navigation bar on the left.',
    hideFromMenu: 'Do not show in the left menu',
    hideFromMenuHint: 'The page stays the home page but does not appear as a menu item for visitors.',

    showsFirstTab: 'This page shows the first tab',
    showsFirstTabHint: 'A page with tabs has no content of its own: visitors automatically see the first tab. Open a tab above to edit its content.',

    chooseLayout: 'Choose a layout:',
    cancel: 'Cancel',
    addSectionTop: 'Add section at the top',
    addSectionAfter: 'Add section after section {n}',

    dragToMove: 'Drag to move the section',
    sectionN: 'Section {n}',
    sectionLayout: 'Layout for section {n}',
    sectionKind: 'Type of section {n}',
    sectionKinds: {
      standard: 'Standard (rows)',
      hero: 'Hero',
    },
    moveSectionUp: 'Move section {n} up',
    moveSectionDown: 'Move section {n} down',
    duplicate: 'Duplicate',
    copy: 'Copy',
    duplicateSectionN: 'Duplicate section {n}',
    copySectionN: 'Copy section {n}',
    pasteSectionTop: 'Paste section',
    pasteSectionHere: 'Paste section',
    deleteSectionN: 'Delete section {n}',

    sectionTitle: 'Section title',
    optional: 'Optional',
    sectionTitleHint: 'Shown as a heading above the section on the page. Leave empty to show no title.',
    backgroundColor: 'Background colour',
    sectionBgColor: 'Background colour for section {n}',
    none: 'None',
    sectionBgColorHex: 'Background colour hex value for section {n}',
    sectionBgColorClear: 'Remove background colour of section {n}',
    noBackgroundColor: 'No background colour',

    sectionSettings: 'Section settings',
    sectionSettingsN: 'Settings for section {n}',
    toggleSectionN: 'Collapse or expand section {n}',
    collapseAll: 'Collapse all',
    expandAll: 'Expand all',
    sectionSettingsHint: 'These settings apply to this section only and override the site theme. Leave a colour empty to follow the theme.',
    sectionTitleColor: 'Section title colour',
    sectionTitleColorClear: 'Remove title colour of section {n}',
    sectionBorder: 'Border',
    sectionBorderColor: 'Border colour',
    sectionBorderColorClear: 'Remove border colour of section {n}',
    sectionWidth: 'Width',
    sectionWidthStandard: 'Standard width',
    sectionWidthFull: 'Full width',
    sectionWidthHint: 'Full width makes the section span the entire screen, like a hero.',
    sectionCenterContent: 'Centre content',
    sectionCenterContentHint: 'The background keeps spanning the full screen, while the content stays as wide as the other sections.',
    sectionPadding: 'Inner spacing',
    sectionPaddingBreakpoint: 'Screen width for inner spacing',
    sectionPaddingHint: 'Space between the section edge and its content. Empty is the default (24 px).',
    sectionPaddingMobileHint: 'Applies below 640 pixels. Empty uses the desktop value.',
    desktop: 'Desktop',
    mobile: 'Mobile',
    padding: {
      top: 'Top',
      right: 'Right',
      bottom: 'Bottom',
      left: 'Left',
    },

    rowVisibility: {
      label: 'On which screens row {n} appears',
      all: 'Show everywhere',
      desktop: 'Desktop only',
      mobile: 'Mobile only',
      hidden: 'Hidden',
      hiddenBadge: 'Hidden',
      hiddenRowLabel: 'Row {n}, hidden from visitors',
    },
    rowSettings: 'Row settings',
    rowSettingsN: 'Settings for row {n}',
    rowSettingsHint: 'These settings apply to this row only. Leave a colour empty to follow the theme.',
    sectionBgImage: {
      label: 'Background image',
      upload: 'Click to upload a background image',
      replace: 'Replace',
      remove: 'Remove',
      fit: 'Display',
      contrastHint: 'Check that the text stays readable on top of it.',
      fits: {
        cover: 'Fill the section',
        contain: 'Fit inside',
        original: 'Original size',
        repeat: 'Repeat as tiles',
      },
      aligns: { top: 'Top', center: 'Middle', bottom: 'Bottom' },
    },
    rowBackground: {
      colorClear: 'No background colour for row {n}',
    },
    rowPadding: {
      label: 'Space inside',
      labelN: 'Space inside row {n}',
    },
    rowBorder: {
      label: 'Border',
      labelN: 'Border around row {n}',
      color: 'Border colour',
      colorClear: 'Back to the theme colour for row {n}',
    },
    imageLayout: {
      size: 'Width',
      align: 'Horizontal',
      valign: 'Vertical',
      alignDisabled: 'At full width there is nothing left to align.',
      hint: 'Vertical placement shows up when the column next to it is taller.',
      sizes: {
        small: 'Small (1/3)',
        medium: 'Medium (1/2)',
        large: 'Large (3/4)',
        full: 'Full width',
      },
      aligns: { left: 'Left', center: 'Centre', right: 'Right' },
      valigns: { top: 'Top', middle: 'Middle', bottom: 'Bottom' },
      corners: 'Corners',
      radius: 'Radius (px)',
      cornerChoices: {
        theme: 'Follow the theme',
        rounded: 'Rounded',
        square: 'Square',
      },
    },

    heroBackground: 'Background',
    heroColor: 'Colour',
    heroImage: 'Image',
    heroBgColor: 'Background colour',
    heroBgColorHex: 'Background colour hex value',
    heroChangeImage: 'Choose a different image',
    heroUploadImage: 'Click to upload an image',
    heroSizeHint: 'Best results at 1600 x 600 pixels or larger. JPG, PNG or WebP, up to 5 MB.',
    heroFocus: {
      heading: 'Focal point',
      hint: 'The image is cropped to fill the band. Click or drag on the image to choose which part stays in view; you can also use the arrow keys.',
      pickerAria: 'Focal point of the hero image. Click on the image or use the arrow keys.',
      previewLabel: 'How it will be cropped',
      readout: 'Focal point: {x}% from the left, {y}% from the top',
      reset: 'Back to the centre',
    },
    heroAltPlaceholder: 'Alt text (accessibility)',
    heroAltAria: 'Alt text for background image',
    heroHeading: 'Heading',
    heroTitlePlaceholder: 'Hero title',
    heroSubtitle: 'Subtitle',
    heroSubtitlePlaceholder: 'Short subtitle below the heading',
    heroTextColor: 'Text colour',
    heroLight: 'Light',
    heroDark: 'Dark',

    row: 'Row {n}',
    rowLayout: 'Layout for row {n}',
    lastRowHint: 'A section needs at least one row — delete the section instead.',
    dragToMoveRow: 'Drag to move the row',
    moveRowUp: 'Move row {n} up',
    moveRowDown: 'Move row {n} down',
    duplicateRowN: 'Duplicate row {n}',
    copyRowN: 'Copy row {n}',
    deleteRowN: 'Delete row {n}',
    addRow: 'Add row',
    pasteRow: 'Paste row',
    addRowAtStart: 'Add a row before row 1',
    addRowAtN: 'Add a row after row {n}',
    pasteRowAtStart: 'Paste the copied row before row 1',
    pasteRowAtN: 'Paste the copied row after row {n}',
    chooseRowLayout: 'Choose a layout for the new row:',

    column: 'Column {n}',
    columnOf: 'Column {n} of {total}',
    columnBgColor: 'Background colour for column {n}',
    columnBgColorHex: 'Background colour hex value for column {n}',
    columnBgColorClear: 'Remove background colour of column {n}',
    columnSettings: 'Column settings',
    columnSettingsN: 'Settings for column {n}',
    columnSettingsHint: 'Applies to this column only. Leave empty to follow the standard styling.',
    columnBorder: 'Border',
    columnBorderN: 'Border around column {n}',
    columnBorderReset: 'Automatic',
    columnBorderResetN: 'Back to automatic border for column {n}',
    columnBorderAutoHint: 'Automatic: text, card and image columns have no frame; other content types do.',
    columnBorderOwnFrameHint: 'A call-out or divider draws its own frame, so this column has no border or padding.',
    columnBorderColor: 'Border colour',
    columnBorderColorClear: 'Remove border colour of column {n}',
    dragToMoveColumn: 'Drag to move the column',
    moveColumnLeft: 'Move column {n} left',
    moveColumnRight: 'Move column {n} right',
    deleteColumnN: 'Delete column {n}',
    lastColumnHint: 'A row needs at least one column — delete the row instead.',
    contentTypeForColumn: 'Content type for column {n}',

    textContentForColumn: 'Text content for column {n}',
    textPlaceholder: 'Enter text (Markdown supported)…',
    markdownHint: 'Markdown is supported: **bold**, *italic*, # headings, - lists',

    removeImage: 'Remove image',
    uploading: 'Uploading…',
    cardUploadImageOptional: 'Click to upload an image (optional)',
    altText: 'Alt text (accessibility)',
    describeImage: 'Describe the image…',
    cardText: 'Text (optional)',
    cardLink: 'Link (optional) — makes the whole card clickable',
    linkPlaceholder: '/pages/12 or https://example.com',

    uploadedImageAlt: 'Uploaded image',
    uploadImage: 'Click to upload an image',
    caption: 'Caption (optional)',
    captionPlaceholder: 'Caption below the image…',
    imageLink: 'Link (optional) — makes the image clickable',

    codeForColumn: 'Code for column {n}',
    codePlaceholder: '// Enter code…',

    colorName: 'Colour name',
    hexValue: 'Hex value',
    unnamed: 'Unnamed',

    previewLoading: 'Preview is loading…',
    removeFigmaDesign: 'Remove Figma design',
    displaySize: 'Display size',
    chooseOtherDesign: 'Choose a different design',
    chooseFigmaDesign: 'Choose a Figma design',
    browseLibrary: 'Browse the library and pick a screen',

    noSections: 'No sections yet',
    noSectionsHint: 'Add your first section to start building.',
    addFirstSection: 'Add first section',

    sectionTypes: {
      '1-col': '1 Column',
      '2-col': '2 Columns',
      '3-col': '3 Columns',
      '4-col': '4 Columns',
      hero: 'Hero',
    },

    callout: {
      icon: 'Icon',
      uploadIcon: 'Upload your own icon (PNG/WebP)',
      removeIcon: 'Remove icon',
      layout: 'Layout',
      layoutInline: 'Icon left, text right',
      layoutStacked: 'Icon above, text below',
      iconAlign: 'Icon alignment',
      iconVerticalAlign: 'Icon alignment (vertical)',
      alignLeft: 'Left',
      alignCenter: 'Centred',
      alignRight: 'Right',
      alignTop: 'Top',
      alignMiddle: 'Middle',
      alignBottom: 'Bottom',
      iconSize: 'Icon size (px)',
      iconColor: 'Icon colour',
      text: 'Text',
    },

    divider: {
      heightLabel: 'Height (px)',
      showLine: 'Show line',
      showLineHint: 'Turn off to use the divider purely as a spacer, without a visible line.',
      lineColor: 'Line colour',
      preview: 'Preview',
    },

    componentStatus: {
      label: 'Component status',
      hint: '(optional)',
      none: 'No status',
    },

    mcpConnect: {
      explainer:
        'Shows visitors the address they use to connect their own AI tool to this styleguide. The address is determined automatically; you do not have to fill anything in.',
      titleLabel: 'Custom heading (optional)',
      introLabel: 'Custom text (optional)',
      showSteps: 'Show steps',
    },

    storybook: {
      explainer:
        'Shows one story from your published Storybook, so the running component sits right next to the agreements about it.',
      storyLabel: 'Story',
      storyPlaceholder: 'Paste the address of a story, or the story ID',
      storyHint:
        'Open the story in your Storybook and copy the address from the address bar. The story ID is taken from it automatically.',
      storyResolved: 'Story ID: {id}',
      storyInvalid: 'No usable story found in this. Paste the address of a story from your Storybook.',
      heightLabel: 'Height in pixels',
      notConnected: 'No Storybook connected yet.',
      notConnectedLink: 'Set the address in settings',
      pickerLabel: 'Component',
      pickerPlaceholder: 'Choose a component from your Storybook',
      pickerLoading: 'Fetching components from your Storybook...',
      pickerUnavailable:
        'The list of components could not be fetched from your Storybook. Paste the address of a story below.',
      pickerNotStorybook:
        'There is no Storybook at {url}. If your Storybook lives in a subfolder, include it in the address, for example {url}/storybook. You can change it in',
      pickerRefresh: 'Refresh list',
      docsOption: 'Documentation',
      orPasteLabel: 'Or paste the address of a story',
      embedBlocked:
        'Your Storybook refuses to be shown inside another site, so this block stays empty for your readers. What to do about it is explained in',
      embedBlockedLink: 'Settings, Storybook',
    },

    blockTypes: {
      text: 'Text (Markdown)',
      richtext: 'Text (editor)',
      figma_design: 'Figma Design',
      text_styles: 'Text styles',
      card: 'Card',
      callout: 'Call-out',
      divider: 'Divider',
      image: 'Image',
      code: 'Code',
      color_swatch: 'Colour swatch',
      mcp_connect: 'AI connection',
      storybook: 'Storybook',
    },

    textStyles: {
      choose: 'Choose text styles',
      browse: 'Pick a Figma file and tick the styles',
      chooseOther: 'Choose other styles',
      remove: 'Remove text styles',
      chosenCount: '{count} style(s) from {file}',
      unavailable: 'These styles are no longer available. Choose again.',
      moveUp: 'Move style up',
      moveDown: 'Move style down',
      removeStyle: 'Remove style from this block',
      options: 'Display',
      sampleText: 'Sample text',
      sampleTextPlaceholder: 'Leave empty for the default sentence',
      remBase: 'rem base',
      remBaseHint: 'What the rem values are based on (16px by default)',
      specPosition: 'Specifications',
      specPositionLeft: 'Left',
      specPositionRight: 'Right',
      specPositionAbove: 'Above',
      visibleSpecs: 'Visible rows',
    },

    colorSwatch: {
      source: 'Source',
      sourceManual: 'Manual',
      sourceFigma: 'From Figma',
      chooseStyle: 'Choose style',
      changeStyle: 'Choose another style',
      detach: 'Detach',
      detachHint: 'Turns this into a standalone, manual swatch — a later change in Figma will no longer update it.',
      unavailable: 'This colour style is no longer available in Figma.',
    },

    sizes: {
      small: 'Small',
      medium: 'Medium',
      large: 'Large',
    },

    lock: {
      bannerTitle: '{name} is editing this page',
      bannerBody: 'You can watch along, but you cannot edit until they are done. This screen unlocks automatically.',
      takeover: 'Take over editing',
      confirmTakeover: 'Take over editing from {name}? Their unsaved changes may be lost.',
      available: 'This page is available again — you can edit now.',
      someone: 'Someone else',
      saveBlocked: 'Could not save: {name} has taken over editing this page.',
    },

    revisions: {
      editedBy: 'Edited {time} by {name}',
      edited: 'Edited {time}',
      title: 'Version history',
      shortLabel: 'History',
      intro: 'Every save creates a version. Open one to view it, then restore it if you want.',
      close: 'Close version history',
      empty: 'No earlier versions yet. Once you save this page, they will appear here.',
      current: 'Current version',
      causes: {
        draft: 'Draft saved',
        published: 'Published',
        restore: 'Version restored',
      },
      by: 'by {name}',
      view: 'View version from {time}',
      previewBannerTitle: 'You are viewing the version from {time}',
      previewBannerBody: 'This is a read-only view — nothing has changed yet. Restore this version to make it the current one, or go back.',
      restore: 'Restore this version',
      restoring: 'Restoring…',
      exitPreview: 'Back to current version',
      confirmRestore: 'Replace the current content with this version? The current version stays in the history, so you can always go back.',
      restored: 'Earlier version restored.',
      restoreFailed: 'Could not restore this version',
      loadFailed: 'Could not load this version',
      timeJustNow: 'just now',
      timeMinutes: '{n} minute ago | {n} minutes ago',
      timeHours: '{n} hour ago | {n} hours ago',
      timeDays: '{n} day ago | {n} days ago',
    },

    messages: {
      confirmUnsaved: 'You have unsaved changes. Are you sure you want to leave?',
      unsavedWarning: 'You have unsaved changes.',
      confirmDeleteSection: 'Are you sure you want to delete this section?',
      confirmDeleteRow: 'Are you sure you want to delete this row and its content?',
      confirmDeleteColumn: 'Are you sure you want to delete this column and its content?',
      columnMovedTo: 'Column moved to position {pos} of {total}.',
      columnMoved: 'Column moved.',
      columnDeleted: 'Column deleted.',
      rowAdded: 'Row added.',
      rowDeleted: 'Row deleted.',
      sectionMovedTo: 'Section moved to position {pos} of {total}.',
      sectionMoved: 'Section moved.',
      sectionDuplicated: 'Section duplicated.',
      sectionCopied: 'Section copied.',
      copyFailed: 'Could not copy: this content cannot be copied.',
      copiedSessionOnly: 'Copied — available in this tab only (storage is full).',
      sectionPasted: 'Section pasted.',
      rowMovedTo: 'Row moved to position {pos} of {total}.',
      rowMoved: 'Row moved.',
      rowDuplicated: 'Row duplicated.',
      rowCopied: 'Row copied.',
      rowPasted: 'Row pasted.',
      homepageSet: 'Set as homepage',
      homepageUnset: 'Homepage designation removed',
      homepageChangeFailed: 'Could not change homepage',
      sidebarHidden: 'Left menu hidden on the home page',
      sidebarVisible: 'Left menu visible again',
      settingChangeFailed: 'Could not change setting',
      hiddenFromMenu: 'Hidden from the left menu',
      visibleInMenu: 'Visible again in the left menu',
      visibilityChangeFailed: 'Could not change visibility',
      imageUploaded: 'Image uploaded!',
      uploadFailed: 'Upload failed',
      changesUpdated: 'Changes updated',
      pagePublished: 'Page published!',
      revertedToDraft: 'Reverted to draft',
      savedAsDraft: 'Saved as draft',
      saveFailed: 'Save failed',
    },
  },

  notFound: {
    title: 'Page not found',
    body: 'The page you are looking for does not exist or has moved.',
    home: 'Go to home',
    back: 'Back',
    cantFind: "Can't find what you're looking for?",
    viewAll: 'View all pages',
  },

  unknownEnvironment: {
    title: 'This environment does not exist',
    body: 'No documentation belongs to this web address. Check whether the address is correct — a typo in the name is the most common cause.',
    hint: 'Sure the address is right? Then check with whoever sent you the link.',
  },

  a11yStatement: {
    title: 'Accessibility statement',
    intro: 'We strive to make Pentrical accessible to everyone, including people with disabilities.',
    complianceHeading: 'Compliance status',
    compliancePre: 'This platform conforms to the',
    wcagLink: 'Web Content Accessibility Guidelines (WCAG) 2.2 level AA',
    measuresHeading: 'Accessibility measures',
    measuresIntro: 'We take the following measures to ensure accessibility:',
    measures: [
      'Accessibility is part of our development processes',
      'All new features are tested for accessibility',
      'We use semantic HTML',
      'All interactive elements are reachable via the keyboard',
      'Colour contrasts meet WCAG AA standards (at least 4.5:1 for text)',
      'Clickable and touchable elements are at least 24x24px (WCAG 2.5.8)',
      'Forms have clear labels and error messages',
      'Images have alternative text',
      'The interface works with screen reader software',
    ],
    techHeading: 'Assistive technologies',
    techIntro: 'This platform has been tested with the following technologies:',
    tech: [
      'NVDA (screen reader) with Firefox',
      'VoiceOver (screen reader) with Safari',
      'Keyboard navigation in all modern browsers',
      'Browser zoom up to 200%',
    ],
    limitationsHeading: 'Known limitations',
    limitationsIntro: 'Despite our efforts, some parts may not yet be fully accessible:',
    limitationFigmaLabel: 'Figma embeds:',
    limitationFigmaBody:
      "Embedded Figma designs depend on Figma's accessibility level. We provide alternative text and specifications.",
    limitationCodeLabel: 'Code syntax highlighting:',
    limitationCodeBody:
      'Some colour combinations in code blocks may have insufficient contrast. The code is still readable and copyable, however.',
    contactHeading: 'Feedback and contact',
    contactIntro:
      'We welcome your feedback on the accessibility of this platform. If you experience problems or have suggestions:',
    contactEmailLabel: 'Email:',
    contactPhoneLabel: 'Phone:',
    contactResponse: 'We aim to respond to your feedback within 5 working days.',
    enforcementHeading: 'Enforcement procedure',
    enforcementBody:
      'If you are not satisfied with our response to your feedback, you can contact the relevant supervisory authority for digital accessibility in your country.',
    techSpecsHeading: 'Technical specifications',
    techSpecsIntro: 'The accessibility of this platform relies on the following technologies:',
    techSpecsOutro:
      'These technologies are used in combination with the accessibility features of browsers and screen reader software.',
    evalHeading: 'Evaluation method',
    evalIntro: 'This platform has been evaluated using:',
    eval: [
      'Automated tests (axe-core, Lighthouse)',
      'Manual testing by experts',
      'Testing with screen reader software',
      'Testing with keyboard navigation only',
    ],
    updateHeading: 'Last update',
    updatePre: 'This statement was last updated on',
    updateDate: '1 January 2024',
  },

  routeTitles: {
    insights: 'Analytics',
    hubs: 'Hubs',
    home: 'Home',
    landing: 'Pentrical — design system documentation',
    terms: 'Terms & conditions',
    privacy: 'Privacy policy',
    disclaimer: 'Disclaimer',
    login: 'Log in',
    forgotPassword: 'Forgot password',
    resetPassword: 'Reset password',
    handoff: 'Signing you in',
    checkout: 'Payment',
    signup: 'Create account',
    invitation: 'Accept invitation',
    gettingStarted: 'Getting started',
    pages: 'Pages',
    page: 'Page',
    docs: 'Documentation',
    pageEditor: 'Edit page',
    figmaLibrary: 'Figma library',
    tokens: 'Design tokens',
    theme: 'Theme',
    access: 'Access',
    profile: 'Profile',
    team: 'Team',
    communication: 'Communication',
    accessibility: 'Accessibility',
    billing: 'Subscription & billing',
    mcpServer: 'Pentrical MCP Server',
    storybook: 'Storybook',
    about: 'About',
    figmaFile: 'Figma file',
    figmaViewer: 'Interactive viewer',
    accessibilityStatement: 'Accessibility statement',
    notFound: 'Page not found',
  },

  a11y: {
    routeAnnounce: 'Navigated to {title}',
    routeAnnounceFallback: 'new page',
    skipToContent: 'Skip to content',
    menu: 'Menu',
    backToTop: 'Back to top',
    mainNav: 'Main navigation',
    mainNavMobile: 'Main navigation (mobile)',
    // Meldingen die alleen een schermlezer voorleest (WCAG 4.1.3).
    announce: {
      defaultItem: 'Content',
      loading: '{item} is loading…',
      loaded: '{item} has loaded',
      success: 'Success: {action}',
      error: 'Error: {error}',
      listNone: 'No {items} found',
      listCount: '{count} {items} found',
      navigation: 'Navigated to {destination}',
      dialogOpen: 'Dialog opened: {title}',
      dialogClose: 'Dialog closed',
      formSubmitting: 'Form is being submitted…',
      formSubmitted: 'Form submitted successfully',
      copied: '{item} copied to the clipboard',
    },
    notificationTypes: {
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Information',
    },
  },

  landing: {
    hero: {
      eyebrow: 'For design and development teams',
      titleLine1: 'Your design system,',
      titleAccent: 'always up to date.',
      titleLine3: 'Without the manual work.',
      sub: 'Pentrical connects Figma to living documentation. Developers click a design and instantly see measurements, colours and tokens — guidelines and screens in one place that keeps itself up to date.',
      ctaPrimary: 'Start your 14-day free trial',
      ctaSecondary: 'See how it works',
      chipsLabel: 'No barriers',
      chipNoCard: 'No credit card required',
      chipReady: 'Ready in 2 minutes',
      chipEu: 'EU hosting',
      mockUrl: 'yourteam.pentrical.com',
      mockSave: 'Save',
      mockEmail: 'Email address',
    },

    specs: {
      buttonName: 'Button / Primary',
      swatchName: 'Token / primary-300',
      inputName: 'Input / Text',
      fill: 'Fill',
      text: 'Text',
      radius: 'Radius',
      padding: 'Padding',
      contrast: 'Contrast',
      value: 'Value',
      usage: 'Usage',
      usageValue: 'accent, illustration',
      textOn: 'Text on top',
      alsoAs: 'Also as',
      alsoAsValue: 'CSS var + Figma style',
      height: 'Height',
      border: 'Border',
      focus: 'Focus',
    },

    pains: {
      title: 'Sound familiar?',
      sub: "Design systems don't age in Figma — they age in the documentation around them.",
      retype: {
        title: 'Retyping specs from Figma',
        text: 'Developers ask for measurements and colours, designers look them up. Every single time.',
      },
      stale: {
        title: 'Documentation that dates instantly',
        text: 'The wiki was right when it was written. Three sprints later, nobody trusts it.',
      },
      truth: {
        title: 'Everyone on a different truth',
        text: 'Design in Figma, guidelines in Notion, tokens in a spreadsheet. Which one is the source?',
      },
    },

    demo: {
      title: 'Click an element. Read the specs.',
      sub: 'This is the heart of Pentrical — try it yourself below.',
      canvasLabel: 'Example design — pick an element to see its specifications',
      save: 'Save',
      swatchSr: 'Colour token primary-300',
      email: 'Email address',
    },

    steps: {
      connect: {
        title: 'Connect your Figma account',
        text: 'Sign in with Figma and pick the files you want to document. No plugins, no exports.',
      },
      sync: {
        title: 'Pentrical syncs',
        text: 'Screens, components, positions and design tokens are pulled in automatically and kept up to date.',
      },
      read: {
        title: 'Your team clicks and reads',
        text: 'Developers click an element and see the specs straight away. Guidelines and designs sit side by side.',
      },
    },

    features: {
      eyebrow: 'All-in-one platform',
      titlePre: 'Everything your',
      titleAccent: 'design system',
      titlePost: 'needs',
      clickable: {
        title: 'Clickable designs with specs',
        text: 'Click an element in the design and see measurements, colours, typography and spacing straight away — without opening Figma.',
      },
      current: {
        title: 'Always up to date',
        text: 'Automatic syncing with Figma keeps previews and specs current, with no manual work.',
      },
      tokens: {
        title: 'Design tokens',
        text: 'Colours, typography and spacing as reusable tokens, straight from your design files.',
      },
      together: {
        title: 'Documentation and designs together',
        text: 'Write guidelines and embed Figma screens directly in your pages. One source for the entire design system.',
      },
      space: {
        title: 'Your own space for your team',
        text: 'Your own subdomain with roles and permissions: the team manages, the whole organisation reads along.',
      },
      hosting: {
        title: 'European hosting',
        text: 'Your data lives on servers within the EU and payments run through Paddle as merchant of record.',
      },
    },

    // A 'testimonials' block with three invented customer quotes from
    // "Name Surname" at "Company" used to live here. The section was already
    // hidden, but the strings still shipped in the JS bundle visitors
    // download. Removed; only restore with real, signed-off quotes.

    reasons: {
      title: 'Why Pentrical?',
      sub: 'There are plenty of wikis. Pentrical is built for exactly one thing: design systems that hold up.',
      built: {
        title: 'Built for design systems',
        text: 'Not a generic wiki with pictures, but a tool that genuinely understands components, tokens and specs.',
      },
      source: {
        title: 'Figma as the source, not an attachment',
        text: 'The connection is the heart of the product. Your documentation follows your designs — not the other way round.',
      },
      european: {
        title: 'European, from hosting to billing',
        text: 'EU servers, GDPR-compliant, fonts without Google, payments and VAT handled neatly through Paddle.',
      },
      noLockin: {
        title: 'No lock-in',
        text: 'Your content stays yours. After cancelling, you can view and export everything for 30 days.',
      },
    },

    pricing: {
      title: 'Clear pricing, no surprises',
      sub: 'Every sign-up starts with a 14-day free trial at Starter level.',
      periodLabel: 'Billing period',
      monthly: 'Monthly',
      annually: 'Annually',
      annualBadge: '2 months free',
      perMonth: '/month',
      free: {
        name: 'Free',
        price: '€0',
        note: 'Free forever, for small systems',
        cta: 'Start for free',
        points: ['1 user', '1 hub', '5 Figma files', 'Interactive viewer', 'Public documentation site'],
      },
      starter: {
        name: 'Starter',
        badge: 'Most popular',
        priceMonthly: '39',
        priceYearly: '32.50',
        noteYearly: '€390 billed per year · excl. VAT',
        noteMonthly: 'Excl. VAT · cancel monthly',
        cta: 'Start your 14-day free trial',
        points: ['3 users', '2 hubs', '50 Figma files', '5 GB storage', 'Automatic syncing', 'Everything in Free'],
      },
      enterprise: {
        name: 'Enterprise',
        price: 'Custom',
        note: 'For larger organisations',
        cta: 'Book a call',
        points: [
          'Unlimited users and files',
          'SSO and custom role arrangements',
          'Custom domain',
          'Data processing agreement and SLA',
          'Personal onboarding',
        ],
      },
      guaranteesLabel: 'Guarantees',
      guarantees: [
        '14-day free trial',
        'No credit card required',
        'Cancel monthly',
        '30 days to export after cancelling',
      ],
    },

    faq: {
      title: 'Frequently asked questions',
      items: [
        {
          q: 'What exactly is Pentrical?',
          a: 'Pentrical is a documentation platform for design systems. It connects your Figma files to written guidelines: developers click a design and instantly see measurements, colours, typography and design tokens — always in sync with the source.',
        },
        {
          q: 'Do I need a credit card to start?',
          a: 'No. Every sign-up starts with a 14-day free trial at Starter level, without any payment details. After that you choose: pay, the free plan, or stop.',
        },
        {
          q: 'Can I cancel monthly?',
          a: 'Yes. The monthly plan can be cancelled each month, the annual plan each year. After cancelling, your documentation stays readable and exportable for another 30 days.',
        },
        {
          q: 'How does payment work?',
          a: 'Payments run through Paddle, our merchant of record. Paddle handles the invoicing and VAT; you automatically receive an invoice by email.',
        },
        {
          q: 'What happens to my data if I stop?',
          a: 'After your subscription ends, you can view and export everything for 30 days. After that your space is retained but closed, and eventually permanently deleted.',
        },
        {
          q: 'Does Pentrical work with any Figma plan?',
          a: 'Yes. You connect your own Figma account via OAuth; Pentrical reads the files you have access to. No Figma Enterprise plan is required.',
        },
      ],
    },

    contact: {
      title: 'Talk to us',
      body: 'Questions about Enterprise, a demo for your team, or just want to talk through your design system? Send a message — we usually reply within one working day.',
      altPrefix: 'Prefer to email directly?',
      subjects: {
        enterprise: 'Enterprise',
        demo: 'Request a demo',
        support: 'Support',
        other: 'Other',
      },
      name: 'Name',
      email: 'Email address',
      company: 'Company',
      optional: '(optional)',
      subject: 'Subject',
      message: 'Message',
      website: 'Website',
      checkFields: 'Please check the highlighted fields.',
      sendFailed: 'Sending failed. Please try again later or email {email}.',
      sending: 'Sending…',
      send: 'Send message',
      sentTitle: 'Message sent',
      sentBody: 'Thanks! We usually reply within one working day at {email}.',
    },

    closing: {
      title: 'Try Pentrical free for 14 days',
      body: 'No credit card required. Your team has its own space within two minutes.',
      cta: 'Create account',
    },

    footer: {
      tagline: 'Living documentation for design systems. Built and hosted in Europe.',
      product: 'Product',
      howItWorks: 'How it works',
      company: 'Company',
      legal: 'Legal',
      terms: 'Terms & conditions',
      privacy: 'Privacy policy',
      disclaimer: 'Disclaimer',
      rights: '© {year} Pentrical',
      madeWith: 'Payments via Paddle · Fonts via Bunny (EU) · No Google trackers',
    },

    meta: {
      title: 'Pentrical — living documentation for your design system',
      description: 'Pentrical connects Figma to your design system documentation. Developers click a design and instantly see specs and tokens. 14-day free trial, no credit card required.',
      ogDescription: 'Clickable designs with specs, automatic Figma sync and design tokens in one place. Free to try.',
      ldAppDescription: 'Documentation platform for design systems with automatic Figma syncing, clickable specs and design tokens.',
      offerFree: 'Free',
      offerStarterMonthly: 'Starter (monthly)',
      offerStarterYearly: 'Starter (annual)',
    },
  },

  legal: {
    updated: 'Last updated: {date}',
    // Loopt gelijk op met config/legal.php (terms_version / privacy_version)
    // in de backend; daar wordt vastgelegd wélke versie iemand aanvaardde.
    updatedDate: '31 July 2026',
    back: '← Back',
    navLabel: 'Legal',
    // Let op: '@' is vue-i18n linked-message-syntax; {'@'} rendert het letterlijk.
    // Drie gepubliceerde contactpunten (zie emailParts in LegalView.vue). Ze
    // mogen alle drie naar dezelfde postbus doorsturen; het gaat erom dat het
    // gepubliceerde adres klopt en blijft werken. AANMAKEN VÓÓR LANCERING.
    contactEmail: "support{'@'}woodmontdigital.nl",
    privacyEmail: "privacy{'@'}woodmontdigital.nl",
    securityEmail: "security{'@'}woodmontdigital.nl",
    terms: {
      title: 'Terms & conditions',
      metaDescription:
        'The terms and conditions for business use of Pentrical: service, subscriptions, pricing, cancellation, intellectual property, availability and liability.',
      sections: [
        {
          h: '1. Who we are and what these terms apply to',
          p: 'Pentrical is a trademark of Woodmont Digital, established at Groenhovenweg 85, 2803 DB Gouda, the Netherlands, registered with the Dutch Chamber of Commerce under number 97972495, VAT identification number NL005300877B78. These terms apply to all use of Pentrical and, together with your order, the privacy policy and — where applicable — the data processing agreement, form the entire agreement between your organisation and us. Whoever creates an account does so on behalf of their organisation and warrants they are authorised to do so; that organisation is our contracting party. We expressly reject any purchasing or other terms of your own.',
        },
        {
          h: '2. The service',
          p: 'Pentrical is an online platform for documenting design systems, with a connection to Figma. You get your own environment on a subdomain where your team manages and shares documentation. We continue to develop the service: features may be added, changed or replaced, as long as the core of the service remains intact.',
        },
        {
          h: '3. Business use only',
          p: 'Pentrical is offered exclusively to companies, institutions and persons acting in the course of a profession or business. By creating an account you declare that you are acting in a business capacity and not as a consumer. Consumer rights, including the statutory right of withdrawal for distance contracts, therefore do not apply. All prices stated on the website are exclusive of VAT.',
        },
        {
          h: '4. Account, trial period and free plan',
          p: 'Every sign-up starts with a free fourteen-day trial, without payment details. After that you choose a paid subscription or the free plan. If you do nothing, your environment becomes read-only and is permanently deleted thirty days later; you receive a warning by email beforehand. The free plan has limits on the number of users and files as stated on the website. We may change or discontinue the free plan with at least thirty days notice, so that you can switch or export your data.',
        },
        {
          h: '5. Subscriptions and payment',
          p: 'Paid subscriptions are billed through Paddle, our merchant of record. Paddle handles invoicing and VAT remittance and acts as the selling party for the payment; Paddle terms additionally apply to that transaction. Subscriptions are paid in advance and renew automatically monthly or annually respectively, unless you cancel. You can cancel at any time from your account settings, taking effect at the end of the current billing period; that period simply runs its course. If a payment fails, we may suspend access after notifying you.',
        },
        {
          h: '6. Price changes',
          p: 'We may change our prices. We announce a change by email to the account owner at least thirty days in advance, and it takes effect at the next renewal — for ongoing annual subscriptions, therefore, only at the next annual renewal. If you do not agree with the new price, you can cancel before the effective date, taking effect at the end of the current billing period. If you continue to use the service after that, the new price applies.',
        },
        {
          h: '7. Cancellation, retention and export',
          p: 'After a cancelled subscription ends, your environment remains available read-only for thirty days so you can export your data. After that the environment is closed and retained for a further ninety days; no later than one hundred and twenty days after the subscription ends, all data is permanently deleted. You receive a warning by email before that final deletion. If you take out a new subscription within that period, the environment is fully restored. An expired trial account is permanently deleted thirty days after the trial period ends.',
        },
        {
          h: '8. Refunds',
          p: 'Amounts paid are not refunded, including on early cancellation; the free trial period is intended for evaluating the service beforehand. If something does not work as agreed, contact us at %EMAIL% — we will look for a suitable solution together. Your statutory rights in the event of a failure on our part remain unaffected.',
        },
        {
          h: '9. Intellectual property',
          p: 'All rights to the platform itself — software, design, documentation and the Pentrical trademark — belong to Woodmont Digital or its licensors. For the duration of the agreement you receive a non-exclusive, non-transferable right to use the service within your own organisation. All rights to the content you place in your environment remain entirely with you or your licensors. You grant us only the limited right to store, copy, process and display that content to the extent necessary to provide, secure and back up the service. That right ends as soon as your data is deleted.',
        },
        {
          h: '10. Your content and responsibilities',
          p: 'You are responsible for the content you place in your environment and warrant that you hold the necessary rights. You keep login details confidential and are responsible for what happens under your accounts. The following are not permitted: infringing third-party rights, placing unlawful or criminal content, disrupting or overloading the service or infrastructure, circumventing security measures, or reselling or making the service available to third parties outside your own organisation. You indemnify us against third-party claims arising from your content or from use in breach of these terms.',
        },
        {
          h: '11. Competing use',
          p: 'You may not use the Service — including the export function and, where available, any API — and may not have it used through a third party, to build or market a competing product, to benchmark or otherwise competitively analyse Pentrical, or to extract data in an automated or bulk manner outside the regular export function. Our prior written consent is required for an integration or tool primarily intended to move data from Pentrical to a competing platform. This article does not limit your own right under article 9 to use, export and take your own content to another service: it targets the building of tooling against Pentrical, not your freedom as a customer to switch providers.',
        },
        {
          h: '12. Personal data and data processing agreement',
          p: 'For the data we process about you ourselves — account details, billing and log files — we are the controller; how we handle it is set out in our privacy policy. If the content you place in your environment contains personal data, you are the controller for it and we are the processor. Our data processing agreement applies to that processing; you receive it on request via %EMAIL_PRIVACY% and it forms an integral part of this agreement.',
        },
        {
          h: '13. Third-party services',
          p: 'The service uses third-party services, including Figma for synchronising designs, Paddle for payments and Brevo for email. The Figma connection is established with your own Figma account and on the basis of your agreement with Figma; we have no influence over their terms, rates, availability or changes to their API. If a third party changes or discontinues its service, we may adapt or discontinue the affected functionality. We are not liable for outages or changes at these third parties; in the event of a material change we will inform you and you may cancel with effect from the end of the current billing period.',
        },
        {
          h: '14. Availability and maintenance',
          p: 'We make every effort to keep the service continuously available, but give no guaranteed availability: there is no SLA. The service may be temporarily unavailable for maintenance; we announce planned maintenance in advance where possible and carry it out outside office hours as far as we can. We provide support by email on working days, without a guaranteed response time.',
        },
        {
          h: '15. Suspension and termination',
          p: 'We may suspend access to an account in the event of overdue payment, a genuine security risk, or conduct in breach of article 10 or 11. Except where immediate action is required, we will warn you first and give you a reasonable period to remedy the situation. If no remedy follows, we may terminate the agreement in writing; the retention and export periods in article 7 continue to apply, unless deletion is required by law.',
        },
        {
          h: '16. Liability',
          p: 'Our liability is limited, per event and per calendar year, to the amount you paid for the service in the twelve months preceding the event causing the damage, with a minimum of one hundred euros. We are not liable for indirect damage, including lost profits, missed savings, reputational damage, loss of data and business interruption. These limitations do not apply in the event of intent or deliberate recklessness on our part. A claim lapses if you do not report it to us in writing within twelve months of discovering the damage. Making your own backups of your content remains your responsibility: we make backups for our own operations, not as an archiving service for you.',
        },
        {
          h: '17. Force majeure',
          p: 'In the event of force majeure we are not obliged to perform our obligations. This includes in any case: failure of hosting, network or power supply, disruptions at suppliers as referred to in article 13, cyberattacks, government measures and labour disputes. If the force majeure lasts longer than thirty days, either party may terminate the agreement without becoming liable for damages.',
        },
        {
          h: '18. Changes to these terms',
          p: 'We may change these terms. We announce material changes by email to the account owner at least thirty days in advance. If you do not agree with a material change, you can cancel before the effective date, taking effect at the end of the current billing period. At registration we record which version of these terms you accepted.',
        },
        {
          h: '19. Governing law and disputes',
          p: 'This agreement is governed exclusively by Dutch law; the Vienna Sales Convention does not apply. Disputes are submitted to the competent court of the District Court of The Hague, without prejudice to our right to bring proceedings before the court of your place of business. The Dutch text of these terms prevails; translations are provided for information only.',
        },
      ],
    },
    privacy: {
      title: 'Privacy policy',
      metaDescription:
        'How Pentrical handles your data: what we process, on what legal basis, which processors we engage, transfers outside the EEA, retention periods, cookies and your rights.',
      sections: [
        {
          h: '1. Who we are',
          p: 'Pentrical is a trademark of Woodmont Digital, established at Groenhovenweg 85, 2803 DB Gouda, the Netherlands, registered with the Dutch Chamber of Commerce under number 97972495, VAT identification number NL005300877B78. For questions about privacy or to exercise your rights, email %EMAIL_PRIVACY%. We have not appointed a data protection officer; this is not legally required for an organisation of this size with these processing activities.',
        },
        {
          h: '2. Our role: controller and processor',
          p: 'For the data we need in order to provide and bill the service — account details, payment details and technical log files — we are the controller. This policy concerns that data. For the content customers place in their own environment we are the processor: if that content contains personal data, the customer determines the purpose and means and we merely carry out the processing. Our data processing agreement applies to that, which customers receive on request.',
        },
        {
          h: '3. What data we process',
          p: 'Account details: name, email address, encrypted stored password, the name of your environment, your role in the team and your preferences for language, display and notifications. Registration details: the moment you accepted the terms, which version, and the IP address at that moment. Usage data: an activity log of which action was performed when and by which user, including IP address and browser type, and the number of failed login attempts. Payment details: name, billing address, country and VAT number insofar as Paddle reports these back to us — we do not see your card or bank details. Content: the documentation, designs and previews you place yourself or synchronise via your own Figma connection. In addition, technical server log files, which may temporarily contain IP addresses and error messages. Your name and email address are required to create an account; without them we cannot provide the service.',
        },
        {
          h: '4. Purposes and legal bases',
          p: 'To perform the agreement (article 6(1)(b) GDPR) we create your account, enable you to sign in, display your environment, synchronise with Figma and send service emails such as verification, subscription warnings and reminders before deletion. On the basis of a legitimate interest (article 6(1)(f) GDPR) we keep an activity log and record failed login attempts, in order to prevent abuse and investigate incidents; our interest in a secure and traceable platform outweighs the limited intrusion into your privacy here. Billing and retaining our administration are carried out to comply with a legal obligation (article 6(1)(c) GDPR). We do not sell data and do not use it for advertising or profiling.',
        },
        {
          h: '5. Processors and transfers outside the EEA',
          p: 'Hosting: Hetzner Online GmbH (Germany) — all servers and databases are located within the EU. Email: Brevo, Sendinblue SAS (France). Payments: Paddle.com Market Ltd (United Kingdom) as merchant of record; an adequacy decision of the European Commission applies to the United Kingdom. Fonts: Bunny Fonts, BunnyWay d.o.o. (Slovenia), which sets no cookies and keeps no visitor records. DNS and certificates: Cloudflare. If you connect a Figma file, your Figma access token and the retrieved design data are exchanged with Figma, Inc. in the United States; that transfer relies on the EU-US Data Privacy Framework and, where necessary, on the European Commission standard contractual clauses. Data processing agreements are in place with all our processors. Apart from the above, no transfer takes place outside the European Economic Area.',
        },
        {
          h: '6. Retention periods',
          p: 'Your data is kept for as long as your account exists. If the trial period expires without a subscription, your environment becomes read-only and is permanently deleted thirty days later — forty-four days after registration in total. If you cancel a paid subscription, your environment remains available read-only for thirty days so you can export, is then closed and retained for a further ninety days, and is permanently deleted no later than one hundred and twenty days after the subscription ends. Technical server log files are kept for fourteen days. The activity log exists for as long as the environment exists and is deleted along with it. Billing records are retained by Paddle as merchant of record; we retain our administration for seven years under the statutory tax retention obligation.',
        },
        {
          h: '7. Cookies and local storage',
          p: 'Pentrical uses no tracking or advertising cookies and does not measure visitor behaviour. We store only what the service needs to work: a session cookie to keep you signed in, and a security token that prevents form abuse. Visitors to a password-protected documentation environment receive a similar cookie, once they submit the correct password, that remembers which sections have already been unlocked; that cookie contains no personal data and, depending on your choice, remains valid for a browser session or for thirty days. We also store your own settings in your browser — language, theme and display preferences — so you do not have to set them again each visit. No consent is required for these; you can delete them via your browser settings, though signing in — or unlocking protected documentation — will then no longer work. On the billing page, Paddle loads its own script to handle payment; this happens only if you open that page yourself. Our fonts come from Bunny Fonts, which sets no cookies and keeps no visitor records.',
        },
        {
          h: '8. Your rights',
          p: 'You have the right of access, rectification, erasure, restriction of processing, objection to processing based on a legitimate interest, and data portability. You can change your name and preferences yourself in your profile; to change your email address, for access, for a full export or for deletion of your account, email %EMAIL_PRIVACY%. We respond within one month. If your account is deleted, the content of your environment goes with it — request an export beforehand if you want to keep it. If you are unhappy with how we handle your data, you can lodge a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens).',
        },
        {
          h: '9. Security and data breaches',
          p: 'Connections are encrypted with TLS, passwords are stored hashed, and customer environments are strictly separated from one another; that separation is tested automatically on every change. Access to production systems is limited to those who need it. If a data breach occurs that poses a risk to your rights and freedoms, we report it to the Dutch Data Protection Authority within seventy-two hours and inform you without undue delay where the breach affects you. If you think you have found a vulnerability, report it via %EMAIL_SECURITY%.',
        },
        {
          h: '10. No automated decision-making',
          p: 'We do not take decisions with legal or similarly significant effects based solely on automated processing, and we do not carry out profiling.',
        },
        {
          h: '11. Changes to this policy',
          p: 'We may amend this privacy policy; the date shown with this policy indicates when that last happened. For material changes we inform account owners by email.',
        },
      ],
    },
    disclaimer: {
      title: 'Disclaimer',
      metaDescription:
        'The disclaimer for pentrical.com: scope, information on this website, customer content, trademarks and liability.',
      sections: [
        {
          h: '1. Scope',
          p: 'This disclaimer applies to the public website pentrical.com and to information shown there. If you hold an active subscription, the terms and conditions prevail wherever they cover the same subject; this disclaimer does not limit any right promised in them.',
        },
        {
          h: '2. Information on this website',
          p: 'We keep the information on this website — feature descriptions, screenshots, prices — up to date to the best of our knowledge, but do not guarantee that it is complete, accurate or current at all times. What you actually receive is set out in your order and the terms and conditions; in case of a difference, those prevail.',
        },
        {
          h: '3. Content placed by customers',
          p: 'Pentrical hosts documentation that customers write, compile and manage themselves within their own environment. We do not review, verify or endorse that content and are not responsible for its accuracy, completeness or lawfulness — including where it concerns accessibility or design guidance. If you believe unlawful content is hosted somewhere, report it via %EMAIL_SECURITY%.',
        },
        {
          h: '4. Not professional advice',
          p: 'Nothing on this website or in documentation hosted through Pentrical is intended or suitable as legal, compliance or other professional advice — including on accessibility, such as WCAG or the European Accessibility Act. Consult a qualified professional for such questions.',
        },
        {
          h: '5. Trademarks',
          p: "Figma is a trademark of Figma, Inc. Pentrical uses that name solely to describe its connection with Figma; Pentrical is an independent company and is not affiliated with, sponsored by, or endorsed by Figma, Inc. Other names and logos mentioned on this website belong to their respective owners.",
        },
        {
          h: '6. External links',
          p: 'This website contains links to third-party services, including Figma. These links are provided for convenience; we have no control over, and accept no responsibility for, the content or privacy practices of those external sites.',
        },
        {
          h: '7. Liability',
          p: 'To the extent permitted by law, use of this website is at your own risk. For customers with an active subscription, the liability arrangement in article 16 of the terms and conditions remains fully in effect; this disclaimer does not replace or limit that arrangement.',
        },
      ],
    },
  },

  auth: {
    common: {
      errorSummaryTitle: 'There were some problems',
      emailLabel: 'Email address',
      emailPlaceholder: "name{'@'}example.com",
      passwordLabel: 'Password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      passwordHint: 'At least 8 characters',
      emailRequired: 'Email address is required',
      emailInvalid: 'Enter a valid email address',
      passwordRequired: 'Password is required',
      passwordMin: 'Password must be at least 8 characters',
      // Shown above the form, before the first field: nobody should have
      // to press submit to find out which fields are required.
      requiredNote: 'Fields with an asterisk are required.',
    },
    brand: {
      headline: 'Your design system, documented and shared.',
      subline: 'One home for tokens, components and Figma — for your whole team.',
      footer: '© Pentrical',
    },
    login: {
      title: 'Log in to Pentrical',
      submit: 'Log in',
      submitting: 'Logging in…',
      successAnnounce: 'Login successful. Redirecting you.',
      failed: 'Login failed',
      forgotPassword: 'Forgot password?',
      noEnvironmentAccess: "You don't have access to this environment. Check the web address, or sign in to the environment you belong to.",
      checkEnvironmentAddress: 'Is the web address of this environment correct? If you belong to a different environment, sign in there.',
      redirecting: 'Signing you in to {environment}…',
      chooseEnvironment: 'Where would you like to go?',
      chooseEnvironmentHint: 'You belong to several environments. Pick one to continue.',
      noEnvironments: "You're signed in, but you don't have an environment yet. Ask a team admin to invite you, or create your own.",
      noAccount: 'No account yet?',
      createFree: 'Create one for free',
    },
    handoff: {
      loadingTitle: 'Signing you in…',
      loadingBody: 'One moment while we open your environment.',
      errorTitle: "Couldn't sign you in",
      failed: 'This sign-in link is no longer valid. Please log in again.',
      missingCode: 'This sign-in link is incomplete. Please log in again.',
      backToLogin: 'Back to login',
    },
    forgot: {
      title: 'Forgot your password?',
      intro: "Enter your email address and we'll send you a link to choose a new one.",
      submit: 'Send reset link',
      submitting: 'Sending…',
      failed: 'Could not send the reset link. Please try again.',
      rateLimited: 'Too many attempts. Wait a minute and try again.',
      backToLogin: 'Back to login',
      success: {
        title: 'Check your email',
        bodyPre: 'If an account exists for ',
        bodyPost: ', we just sent a link to reset your password.',
        hint: "No email after a few minutes? Check your spam folder, or make sure you entered the address you signed up with.",
      },
    },
    reset: {
      title: 'Choose a new password',
      introPre: 'Set a new password for ',
      introPost: '.',
      newPasswordLabel: 'New password',
      confirmLabel: 'Confirm new password',
      submit: 'Reset password',
      submitting: 'Resetting…',
      failed: 'Could not reset your password. Please try again.',
      mismatch: 'The passwords do not match',
      invalidTitle: 'Invalid reset link',
      invalidBody: 'This link is incomplete or has expired. Request a new one to continue.',
      requestNew: 'Request a new link',
      success: {
        title: 'Password reset',
        body: 'Your password has been changed. You can now log in with your new password.',
        toLogin: 'Go to the login page',
      },
    },
    signup: {
      title: 'Create account',
      subtitle: 'Try free for 14 days - no credit card required',
      accountNameLabel: 'Company or team name',
      accountNamePlaceholder: 'Acme Inc.',
      accountNameRequired: 'Company or team name is required',
      subdomainLabel: 'Address of your environment',
      subdomainPlaceholder: 'acme',
      subdomainRequired: 'Subdomain is required',
      subdomainInvalid:
        'At least 2 characters; only lowercase letters, digits and hyphens (not at the edges)',
      subdomainHint: 'This is where you and your team will find the documentation.',
      nameLabel: 'Your name',
      namePlaceholder: 'First and last name',
      nameRequired: 'Your name is required',
      consent: {
        business:
          'Pentrical is a business service: you are creating this account on behalf of your organisation. All prices are exclusive of VAT.',
        termsPre: 'On behalf of my organisation, I agree to the ',
        termsLink: 'terms and conditions',
        termsPost: '.',
        termsRequired: 'You must agree to the terms and conditions to continue',
        // Bewust "gelezen" en niet "akkoord": een privacyverklaring is
        // informatie (AVG art. 13), geen toestemming. De grondslag is de
        // overeenkomst; wie het als toestemming presenteert, moet die ook
        // net zo makkelijk kunnen laten intrekken — en dan valt de dienst om.
        privacyPre: 'I have read the ',
        privacyLink: 'privacy policy',
        privacyPost: '.',
        privacyRequired: 'Please confirm you have read the privacy policy to continue',
      },
      captcha: {
        pending: 'The security check is still running. Wait a moment and try again.',
      },
      submit: 'Create account',
      submitting: 'Creating account…',
      rateLimited: 'Too many attempts. Wait a minute and try again.',
      genericServerError: 'Something went wrong creating your account. Please try again.',
      createdAnnounce: 'Check your email.',
      haveAccount: 'Already have an account?',
      login: 'Log in',
      success: {
        title: 'Check your inbox',
        followLink: 'Follow the link in the email to continue.',
        sentToPre: 'We sent a message to',
        sentToMid: '.',
        noEmail: 'No email received? Check your spam folder.',
        toLogin: 'Go to the login page',
      },
    },
    invite: {
      loadError: 'This invitation could not be loaded.',
      loading: 'Loading invitation…',
      invalidTitle: 'Invitation not valid',
      toLogin: 'Go to login',
      joinTitle: 'Join {account}',
      invitedByText: '{name} invites you',
      invitedGenericText: 'You have been invited',
      collaborateIn: 'to collaborate in',
      nameLabel: 'Name',
      passwordExisting: 'Your password',
      passwordNew: 'Choose a password',
      passwordHint: 'At least 8 characters.',
      passwordMinError: 'Enter a password of at least 8 characters.',
      nameRequiredError: 'Enter your name.',
      acceptFailed: 'Could not accept. Check your details and try again.',
      submitExisting: 'Accept and log in',
      submitNew: 'Create account and join',
    },
  },

  /*
   * Pentrical's own alert bar at the top of the window. Only the chrome is
   * translated: the message itself is typed by the platform administrator
   * and goes out to every hub as written.
   */
  appVersion: {
    // Bewust Engels in beide talen: dit is een bericht van Pentrical zelf.
    message: 'A new version of Pentrical is available.',
    reload: 'Reload the page',
    dismiss: 'Dismiss',
  },
  platformAlerts: {
    variants: {
      // Screen readers only: colour must not be the sole carrier of
      // meaning (WCAG 1.4.1).
      information: 'Notice',
      attention: 'Attention',
      emergency: 'Outage',
    },
    dismiss: 'Dismiss message',
    defaultLinkLabel: 'More information',
  },

  chrome: {
    adminBar: {
      label: 'Admin bar',
      viewAsVisitor: 'View as visitor',
      viewAsVisitorTitle: 'Preview: this is how a visitor sees the site',
      settings: 'Settings',
      openUserMenu: 'Open user menu',
      myAccount: 'My account',
      gettingStarted: 'Getting started',
      help: 'Help',
      logout: 'Log out',
    },
    preview: {
      banner: 'Preview mode — this is exactly what a visitor sees.',
      back: 'Back to management',
    },
    sidebar: {
      label: 'Documentation navigation',
      docs: 'Documentation',
      expand: 'Expand navigation',
      collapse: 'Collapse navigation',
      resizeHandle: 'Adjust navigation width (arrow keys; Enter collapses)',
      loading: 'Loading navigation…',
      loadFailed: 'Could not load the navigation',
      close: 'Close navigation',
    },
    mainLabel: 'Main content',
  },

  nav: {
    // Hoofdnavigatie / categorie-acties (TheHeader)
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    newCategory: 'New category',
    emptyEditor: 'No categories yet. Create one with "New category" at the top.',
    emptyVisitor: 'There is no content here yet.',
    emptyCategoryHint: 'No pages here yet',
    newCategoryPlaceholder: 'Name of new category…',
    newCategoryNameLabel: 'Name new category',
    renameCategoryLabel: 'New category name',
    actionsFor: 'Actions for {name}',
    rename: 'Rename',
    hideFromVisitors: 'Hide from visitors',
    makeVisible: 'Make visible',
    access: 'Access…',
    moveLeft: 'Move left',
    moveRight: 'Move right',
    moveToCategory: 'Move to category',
    cannotMoveHasSubcategories: 'Not possible: this item has subcategories',
    noOtherCategories: 'No other categories',
    deleteWithContent: 'Delete (incl. content)',
    // Documentatieboom (SidebarNav)
    newNameLabel: 'New name',
    newTitleLabel: 'New title',
    addToLabel: 'Add to {name}',
    addPageOrSubcategory: 'Add page or subcategory',
    addPageToLabel: 'Add page to {name}',
    addPageTitle: 'Add page',
    draft: 'Draft',
    newPage: 'New page',
    newSubcategory: 'New subcategory',
    newSubcategoryPlaceholder: 'Name of new subcategory…',
    newSubcategoryLabel: 'Name new subcategory',
    newPagePlaceholder: 'Name of new page…',
    newPageLabel: 'Name new page',
    duplicate: 'Duplicate',
    moveUp: 'Move up',
    moveDown: 'Move down',
    moveTo: 'Move to',
    topLevel: 'Top level',
    noOtherLocations: 'No other locations',
    delete: 'Delete',
    // Meldingen / bevestigingen
    saveFailed: 'Saving failed',
    visibilityFailed: 'Could not change visibility',
    moveFailed: 'Move failed',
    deleteFailed: 'Delete failed',
    duplicateFailed: 'Duplication failed',
    categoryMovedTo: 'Category "{name}" moved to position {position} of {total}',
    movedToCategory: '"{name}" moved to category "{destination}"',
    categoryDeleted: 'Category "{name}" deleted',
    confirmDeleteCategory:
      'Are you sure you want to delete "{name}"? All pages and subcategories within it will also be deleted. This cannot be undone.',
    confirmDeletePageWithTabs:
      'Are you sure you want to delete "{name}"? The {count} tab(s) of this page will also be deleted. This cannot be undone.',
    confirmDeletePage: 'Are you sure you want to delete "{name}"? This cannot be undone.',
  },

  account: {
    title: 'My account',
    nav: {
      profile: 'Profile',
      hubs: 'Hubs',
      team: 'Team',
      communication: 'Communication',
      accessibility: 'Accessibility',
      billing: 'Subscription',
      about: 'About',
    },
    common: {
      save: 'Save',
      prefsSaved: 'Preferences saved',
      saveFailed: 'Saving failed',
      loadFailed: 'Loading failed',
      loading: 'Loading…',
      tryAgainLater: 'Please try again later.',
      cancel: 'Cancel',
    },
    hubs: {
      beschikbaarheid: {
        vrij: 'This address is available.',
        bezet: 'This address is already in use. Choose another name.',
        gereserveerd: 'This address is reserved for the application itself.',
        ongeldig: 'Use only lowercase letters, numbers and hyphens.',
      },
      title: 'Hubs',
      subtitle: 'The documentation sites in this account',
      loading: 'Loading hubs…',
      loadError: 'Could not load the hubs',
      countLimited: '{count} of {limit} hubs in use',
      countUnlimited: '{count} hubs — your plan has no maximum',
      primaryBadge: 'Main address',
      currentBadge: 'You are here',
      contentSummary: '{sections} categories · {pages} pages',
      switchTo: 'Go here',
      makePrimary: 'Put on the main address',
      makePrimaryFor: 'Put {name} on the main address',
      renameFor: 'Edit {name}',
      deleteFor: 'Delete {name}',
      newHub: 'New hub',
      newHeading: 'New hub',
      editHeading: 'Edit hub',
      nameLabel: 'Name',
      slugLabel: 'Address',
      switchFailedTitle: 'Switching did not work',
      switchFailedBody: 'Your sign-in could not be carried over to the other address. You are still signed in here; please try again.',
      slugHint: 'Appears in the web address. Lowercase letters, numbers and hyphens only.',
      save: 'Save',
      saved: 'Hub saved',
      saveFailed: 'Could not save',
      primaryChanged: 'Main address changed',
      limitReached: 'Your plan includes {limit} hub(s) — you are using them all.',
      limitReachedLink: 'See the plans',
      trashHeading: 'Trash',
      trashIntro: 'Deleted hubs are kept for {days} days and can be restored until then.',
      purgeOn: 'will be deleted on {date}',
      restore: 'Restore',
      restored: 'Hub restored',
      restoredFromMail: 'This documentation site is back online and will no longer be removed.',
      restoreFailed: 'Could not restore',
      movedToTrash: 'Moved to trash',
      deleteFailed: 'Could not delete',
      deleteHeading: 'Delete {name}?',
      deleteBody: 'This hub holds {sections} categories and {pages} pages. They are kept for {days} days and can be restored until then; after that they are gone for good.',
      deleteConfirmLabel: 'Type {name} to confirm',
      deleteConfirm: 'Move to trash',
      ownerOnly: 'Only the account owner can manage hubs.',
      switcherLabel: 'Switch hub',
      manage: 'Manage hubs',
    },
    profile: {
      title: 'Profile',
      subtitle: 'Manage your account details',
      nameLabel: 'Name',
      emailHint: 'Your email address is your login name and cannot be changed yet.',
      saved: 'Profile saved',
      savedBody: 'Your profile details have been updated',
      saveError: 'Could not save profile details',
      passwordHeading: 'Change password',
      passwordSubtitle:
        'After changing, your current session stays active and other devices are logged out.',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Repeat new password',
      passwordHint: 'At least 8 characters.',
      passwordMismatch: 'The new passwords do not match.',
      passwordTooShort: 'The new password must be at least 8 characters long.',
      passwordChanged: 'Password changed',
      passwordChangedBody: 'Your new password is active. Sessions on other devices have been logged out.',
      passwordChangeError: 'Could not change the password.',
      changePassword: 'Change password',
    },
    communication: {
      title: 'Communication',
      subtitle: 'Which email this account receives from us',
      marketingTitle: 'News and offers',
      marketingDesc: 'Occasional email about product news and offers. Service messages (maintenance, invoices) are always sent. Applies to the whole account.',
      marketingSavedBody: 'Your email preference has been saved',
      marketingErrorTitle: 'Saving failed',
      marketingErrorBody: 'Your email preference could not be saved. Please try again.',
      notManageable: 'There is nothing to set here for your role. Only the account owner and the billing contact choose which email this account receives.',
    },
    accessibility: {
      title: 'Accessibility',
      subtitle: 'Adjust the display to your preferences',
      savedBody: 'Your accessibility preferences have been updated',
      reduceMotionTitle: 'Reduce motion',
      reduceMotionDesc: 'Reduce animations and transitions',
      highContrastTitle: 'High contrast',
      highContrastDesc: 'Increase contrast for better readability',
      largeTextTitle: 'Large text',
      largeTextDesc: 'Increase text size for better readability',
      statementLink: 'Read our accessibility statement →',
    },
    team: {
      title: 'Team',
      subtitle: 'Manage who has access to this environment',
      loading: 'Loading team details…',
      loadError: 'Could not load the team details',
      seatsOf: 'of',
      seatUnitSingular: 'seat',
      seatUnitPlural: 'seats',
      seatsInUse: 'in use',
      seatsHint: '(members + pending invitations)',
      inviteHeading: 'Invite someone',
      seatsFull:
        'All editor seats are taken. Remove a member or pending invitation first, or purchase another editor.',
      seatsFullLink: 'Go to billing',
      hubAccessAll: 'Access to all hubs',
      hubAccessSome: 'Access to {count} hub(s)',
      hubAccessChange: 'change',
      hubAccessHeading: 'Hub access for {name}',
      hubAccessIntro: 'Choose which documentation sites this person may work in. Anything they cannot see is left out of their overview entirely.',
      hubAccessAllOption: 'All hubs',
      hubAccessAllHint: 'Including hubs you create later.',
      hubAccessSomeOption: 'Only these hubs',
      hubAccessSomeHint: 'The rest stays invisible to this person.',
      hubAccessSave: 'Save access',
      hubAccessSaved: 'Hub access updated',
      hubAccessError: 'Could not change access',
      roleLabel: 'Role',
      member: 'Member',
      owner: 'Owner',
      invite: 'Invite',
      inviteSent: 'Invitation sent',
      inviteError: 'Invitation failed',
      resent: 'Resent',
      resendError: 'Sending failed',
      revoked: 'Revoked',
      revokeError: 'Revoke failed',
      roleChanged: 'Role changed',
      roleChangeError: 'Change failed',
      removed: 'Removed',
      removeError: 'Remove failed',
      confirmRemove: 'Are you sure you want to remove {name} from this account?',
      membersHeading: 'Members',
      you: '(you)',
      makeOwner: 'Make owner',
      makeMember: 'Make member',
      removeLabel: 'Remove {name}',
      pendingHeading: 'Pending invitations',
      invitedAs: 'Invited as {role}',
      roleOwnerLower: 'owner',
      roleMemberLower: 'member',
      validUntil: '· valid until {date}',
      resendLabel: 'Resend invitation',
      revokeLabel: 'Revoke invitation',
      ownerOnly: 'Only the account owner can manage team members.',
    },
    billing: {
      title: 'Subscription & billing',
      subtitle: 'Manage the subscription for this environment',
      loading: 'Loading subscription details…',
      loadError: 'Could not load the subscription details',
      paymentSuccess: 'Payment successful',
      paymentSuccessBody: 'Your subscription is being activated — this takes at most a minute.',
      checkoutError: 'Checkout not started',
      checkoutErrorBody: 'Could not open the payment page. Please try again later.',
      checkoutPage: {
        loadingTitle: 'Opening the payment window…',
        loadingBody: "One moment while Paddle loads. Afterwards you'll go back to your environment.",
        doneTitle: 'Thank you',
        doneBody: 'Paddle has received your payment details. You can close this tab.',
        closedTitle: 'Payment window closed',
        closedBody: 'Nothing has been charged. You can close this tab.',
        errorTitle: "Couldn't open the payment window",
        missingReference: 'This payment link is incomplete. Start the payment again from the billing page in your environment.',
        loadFailed: 'Something went wrong while loading the payment window. Please try again later.',
        backToEnvironment: 'Back to your environment',
      },
      canceled: 'Cancelled',
      cancelError: 'Cancellation failed',
      resumed: 'Resumed',
      resumeError: 'Resume failed',
      switched: 'Switched',
      switchError: 'Could not switch',
      trialHeading: 'Trial period',
      trialLeft: '{days} left (until {date}). Activate a subscription to keep working afterwards.',
      daySingular: 'day',
      dayPlural: 'days',
      trialEnded: 'The trial period has (almost) ended. Activate a subscription to keep working.',
      starterActive: 'Starter plan — active',
      planActive: '{plan} plan — active',
      planEnterprise: 'Enterprise',
      gracePeriod:
        'Cancelled: the subscription runs until {date}. After that the environment becomes read-only.',
      pastDue:
        'The last payment failed. Paddle will retry; update your payment details if needed via the link in the email from Paddle.',
      activeRenews: 'The subscription is running and renews automatically.',
      readOnly: 'Read-only',
      suspended: 'Deactivated',
      readOnlyBody: 'The documentation can still be viewed and exported, but editing is no longer possible.',
      suspendedBody: 'The environment is locked; the data has not been deleted yet.',
      activateToRestore: 'Activate a subscription to restore everything immediately.',
      purgeWarning: 'Without a subscription, this environment will be permanently deleted on {date}.',
      freePlan: 'Free plan',
      freePlanBody: '1 user and up to 5 Figma files. Need more? Upgrade to Starter below.',
      grantedStarterBody:
        'This account has been granted the Starter plan; there is no paid subscription and nothing is charged.',
      active: 'Active',
      activeBody: 'This environment is active.',
      starterLegend: 'Starter plan',
      yearly: 'Yearly',
      twoMonthsFree: '2 months free',
      yearlyPrice: '/year',
      yearlyPriceHint: '(€32.50/mo)',
      monthly: 'Monthly',
      monthlyPrice: '/month',
      activateStarter: 'Activate Starter',
      pricesNote: 'Prices exclude VAT. You pay securely via Paddle; you see the total before confirming.',
      useFreePlan: 'Or use the free plan (1 user, 5 Figma files)',
      freeConfirm:
        'Switch to the free plan? This is only possible if your usage fits within the limits (1 user, 5 Figma files); otherwise we\'ll tell you what needs to go first.',
      yesFreePlan: 'Yes, free plan',
      noCancel: 'Never mind',
      resumeSubscription: 'Undo cancellation',
      cancelSubscription: 'Cancel subscription',
      cancelConfirm:
        'Cancel at the end of the billing period? After that the environment is read-only for 30 days (for export) and the data is eventually deleted.',
      yesCancel: 'Yes, cancel',
      ownerOnly: 'Only the account owner can manage the subscription.',
      periodLegend: 'Billing period',
      planLegend: 'Choose a plan',
      twoMonthsFreeShort: '\u2212 2 mo',
      planStarter: 'Starter',
      planTeam: 'Team',
      teamBadge: 'Up to 10 hubs',
      planStarterSummary: '3 editors, 2 hubs, 50 Figma files',
      planTeamSummary: '10 editors, 10 hubs, analytics and per-hub permissions',
      activatePlan: 'Activate {plan}',
      toYearlyHeading: 'Switch to annual billing',
      toYearlyBody: 'You pay for ten months instead of twelve. The difference is settled right away and the change takes effect immediately.',
      toMonthlyHeading: 'Switch to monthly billing',
      toMonthlyBody: 'You keep the year you have already paid for. Monthly billing starts once that runs out — nothing is charged now.',
      switchToYearly: 'Switch to annual',
      switchToMonthly: 'Switch to monthly',
      intervalChanged: 'Billing period changed',
      intervalChangeError: 'Could not switch',
      switchHeading: 'Switch to {plan}',
      switchToTeamBody: 'Team gives you 10 editors, 10 hubs, analytics and per-hub permissions. Purchased editors that fit within the plan are dropped automatically.',
      switchToStarterBody: 'Starter includes 3 editors and 2 hubs. If you use more editors, those are purchased as extras.',
      switchTo: 'Switch to {plan}',
      planChanged: 'Plan changed',
      planChangeError: 'Could not switch plan',
      seatsHeading: 'Editors',
      seatsOfInUse: 'of {limit} in use',
      seatsOccupancy: '{inUse} of {limit} editor seats taken',
      seatsIncludedRow: 'Included with {plan}',
      seatsPurchasedRow: 'Purchased',
      seatsTotalRow: 'Total available',
      seatsMembersRow: 'Members',
      seatsInvitedRow: 'Invited, not accepted yet',
      seatsFreeRow: 'Still free',
      seatsViewersFree: 'Viewers are always free and never count towards this.',
      seatsExtraLabel: 'Extra editors',
      seatsIncrease: 'Add one editor',
      seatsDecrease: 'Remove one editor',
      seatsPerMonth: 'per month',
      seatsPerYear: 'per year',
      seatsUnitPrice: '{price} {period} per extra editor',
      seatsPreview: 'You are moving to {limit} editors, so {amount} {period} extra.',
      seatsProration: 'The difference is settled on your next invoice.',
      seatsFloor: 'You cannot go lower: {inUse} seats are taken. Remove a member or invitation first.',
      seatsSave: 'Apply change',
      seatsUpdated: 'Editors updated',
      seatsError: 'Could not change editors',
      usageHeading: 'Usage',
      usageStorage: 'Storage',
      usageFiles: 'Figma files',
      usageOf: 'of {limit}',
      usageBar: '{used} of {limit} in use',
      usageStorageNote:
        'A fair use guideline, not a lock. If you go over, everything keeps working; we will get in touch if it stays that way.',
      usageStorageOver:
        'You are over the {limit} guideline. Everything keeps working; we will get in touch if it stays this way.',
      usageFilesNote: 'A new import is refused at {limit} files.',
      usageFilesFull:
        'You have reached the maximum. A new import is refused until you remove a file or move to a larger plan.',
      usageMeasured: 'Storage measured on {date} and updated every night. The file count is live.',
    },
    about: {
      title: 'About',
      subtitle: 'Version information for this environment',
      versionLabel: 'Version',
    },
  },

  access: {
    modes: {
      public: 'Public',
      password: 'Password',
      team: 'Team only',
    },

    adminBar: {
      title: 'Who can read this documentation: {mode}. Click to change.',
    },

    gate: {
      title: 'This documentation is protected',
      categoryTitle: '{name} is protected',
      teamTitle: 'This documentation is for team members',
      body: 'Enter the password you received to continue.',
      categoryBody: 'This category has its own password. Enter it to continue.',
      teamBody: 'Log in with your account to read this documentation.',
      passwordLabel: 'Password',
      remember: 'Remember me on this device',
      submit: 'Unlock',
      unlockSite: 'Unlock',
      wrongPassword: 'That password is not correct.',
      tooManyAttempts: 'Too many attempts. Please wait a minute and try again.',
      noLongerLocked: 'This documentation is no longer protected — reload the page.',
      failed: 'Unlocking failed. Please try again.',
      memberFooter: 'Part of the team?',
    },

    settings: {
      title: 'Access',
      subtitle: 'Decide who can read your documentation.',
      heading: 'Who can read this documentation',
      headingHint: 'Applies to the whole site. Individual categories can deviate from this.',
      publicDesc: 'Anyone with the link can read along.',
      passwordDesc: 'One shared password that you hand out yourself.',
      teamDesc: 'Only people who are logged in and part of your team.',
      statusPublic: 'Your site is public — anyone with the link can read along.',
      statusPassword: 'Your site is protected with a password',
      statusTeam: 'Your site is only available to your team',
      since: ' since {date}.',
      newPassword: 'New password',
      keepPassword: 'Leave empty to keep the current password',
      passwordSet: 'A password is set. Enter a new one to replace it.',
      passwordRule: 'At least 8 characters. This password is shared, so make it easy to pass on.',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      generate: 'Suggest a password',
      hintLabel: 'Hint for visitors (optional)',
      hintPlaceholder: 'Ask your contact person',
      hintHelp: 'Shown on the unlock screen. Never put the password itself here.',
      copyInvite: 'Copy invitation',
      copied: 'Copied',
      copyFailed: 'Could not copy to clipboard.',
      inviteIntro: 'You can read our design system documentation here:',
      invitePassword: 'Password: {password}',
      save: 'Save access settings',
      saved: 'Access settings saved',
      savedBody: 'The new setting applies to visitors immediately.',
      saveFailed: 'Saving failed',
      loadFailed: 'Could not load the access settings',
      sharedCaveat: 'A shared password keeps your documentation out of search engines and away from casual visitors, but anyone who has it can pass it on. For access per person, choose "Team only".',
      categoriesHeading: 'Categories with their own lock',
      categoriesHint: 'Set per category via the ⋯ menu next to its name.',
      noCategoryLocks: 'No category deviates from the site setting.',
      seePlans: 'See plans',
    },

    category: {
      title: 'Access — {name}',
      legend: 'Who can read this category',
      inherit: 'Follows the site',
      inheritNow: 'Currently: {mode}',
      publicDesc: 'Readable even when the rest of the site is protected.',
      passwordDesc: 'Its own password, on top of the site setting.',
      teamDesc: 'Only for people who are logged in and part of your team.',
      newPassword: 'New password',
      keepPassword: 'Leave empty to keep the current password',
      visibilityLegend: 'Show in the menu?',
      visibilityLocked: 'Show with a lock',
      visibilityLockedDesc: 'Readers see that this category exists and can ask for access.',
      visibilityHidden: 'Hide completely',
      visibilityHiddenDesc: 'Invisible to anyone without access.',
      saved: 'Access for {name} saved',
      saveFailed: 'Could not save the access settings',
      planRequired: 'Access per category is part of the Starter plan.',
      lockedLabel: 'Protected',
      onBadge: 'On',
    },
  },

  settings: {
    title: 'Settings',
    insights: {
      title: 'Analytics',
      subtitle: 'Is your design system being used, and where does it fall short',
      loading: 'Loading report…',
      teamHeading: 'Part of the Team plan',
      teamBody: 'Analytics shows what readers search for without finding anything, which Figma components have no documentation yet, and which pages are read but have not been updated in a long time.',
      periodLabel: 'Period',
      days7: 'Past week',
      days30: 'Past month',
      days90: 'Past quarter',
      gapsHeading: 'Searched, nothing found',
      gapsIntro: 'Readers looked for these and got nothing back — gaps in your documentation, in their own words.',
      gapsEmpty: 'Every search returned something.',
      timesSearched: 'searched {count}\u00d7',
      adoptionHeading: 'From Figma to documentation',
      adoptionSummary: '{components} components in Figma, {documented} documented, {read} of those actually read.',
      adoptionEmpty: 'No Figma components in this hub yet. Connect a file to see what is and is not documented.',
      inFigma: 'In Figma',
      undocumentedLabel: 'Undocumented',
      documentedUnreadLabel: 'Documented, unread',
      undocumentedList: 'These components have no page yet:',
      staleHeading: 'Read, but not updated in a while',
      staleIntro: 'These pages are being used while nothing has changed about them for some time.',
      viewsSince: '{count} views',
      updated: 'updated {date}',
      mostReadHeading: 'Most read',
      privacyNote: 'Only counts per page per day are stored. No cookies, no profiles, nothing traceable to an individual visitor.',
    },
    nav: {
      figma: 'Figma library',
      tokens: 'Design tokens',
      theme: 'Theme',
      access: 'Access',
      mcp: 'MCP server',
      insights: 'Analytics',
      storybook: 'Storybook',
    },
    storybook: {
      title: 'Storybook',
      subtitle: 'Show your running components inside the documentation',
      intro:
        'Storybook is the separate site where your built components run on their own. Set its address here once and editors can embed any story on a page, without pasting a full address every time.',
      urlLabel: 'Address of your Storybook',
      urlPlaceholder: 'https://storybook.example.com',
      urlHint:
        'The address where your published Storybook lives. It has to start with https:// and be reachable from the browsers of the people reading your styleguide.',
      save: 'Save',
      saving: 'Saving…',
      saved: 'Storybook address saved',
      disconnect: 'Disconnect',
      disconnected: 'Storybook disconnected',
      connectedLabel: 'Currently connected to',
      notConnected: 'No Storybook connected yet.',
      openStorybook: 'Open Storybook',
      privacyTitle: 'What this does and does not do',
      privacyBody:
        'A story is loaded by the browser of the person reading the page, straight from your Storybook. Pentrical never fetches it, so a Storybook behind your company network keeps working for colleagues on that network and simply stays blank for anyone else.',
      failed: 'The address could not be saved.',
      check: {
        allowed: 'This Storybook can be embedded',
        blocked: 'This Storybook refuses to be embedded',
        unknown: 'We could not check this Storybook',
        notChecked: 'Not checked yet',
        notCheckedBody:
          'We have not looked at whether this Storybook allows itself to be embedded. Some servers refuse that, which leaves a story block empty for every reader.',
        allowedBody: 'Nothing is stopping it. A story placed on a page will show up for your readers.',
        xFrameOptionsBody:
          'The server behind this address tells every browser that its pages may only be framed by itself. A story block will therefore stay empty with a browser error, and no setting on our side can override that — it is the other server that decides.',
        frameAncestorsBody:
          'The server behind this address lists which sites may frame it, and this styleguide is not on that list. A story block will therefore stay empty with a browser error.',
        unreachableBody:
          'Our server could not reach this address. That is not necessarily a problem: a Storybook behind your company network is invisible to us and still works for colleagues on that network, because the story is loaded by their browser and not by us. Check it yourself by putting a story on a page.',
        redirectBody:
          'This address forwards to somewhere else, so we stopped. Fill in the address where the Storybook itself lives, without the forward.',
        headerLabel: 'The server sends:',
        fixIntro: 'Whoever runs that server can allow it with this line, replacing the current one:',
        fixNote:
          'The old X-Frame-Options line has to go, not stay alongside it — with both present, browsers behave differently from one another. The address above is the environment you are looking at right now; if you are working from your own machine, fill in the address your readers will use instead. Several environments may follow one another, separated by a space.',
        recheck: 'Check again',
        checkedAt: 'Checked {when}',
      },
    },
    mcp: {
      scope: 'What may an AI tool read?',
      scopeDesc: 'Switch a category off to keep it away from AI tools. Useful for work that is not ready to leave the building, or agreements you share with your team but not with a language model.',
      scopeHint: 'This changes nothing about what people see in the browser — only what goes to an AI tool.',
      figmaScope: 'Designs an AI tool may read',
      figmaScopeDesc:
        'An AI tool can also request the components themselves: dimensions, colours, spacing and generated code. Switch a file off if it must not go to an external AI provider.',
      figmaToggle: 'Designs from {name} available to AI tools',
      figmaExcludedBody: '{name} is no longer readable by AI tools',
      figmaIncludedBody: '{name} is readable by AI tools again',
      categoryInherited: 'Follows the parent category',
      categoryHidden: 'Hidden from AI',
      categoryToggle: 'AI access to {name}',
      categorySaved: 'Saved',
      categoryExcludedBody: '"{name}" is no longer visible to AI tools.',
      categoryIncludedBody: '"{name}" is visible to AI tools again.',
      categoryFailed: 'The change could not be saved. Please try again later.',
      title: 'Pentrical MCP Server',
      subtitle: 'Let Claude, Cursor or Copilot read directly from this design system',
      explainer:
        'Without a connection you have to copy and paste documentation into an AI tool. With this '
        + 'connection the AI tool looks up the right page itself the moment it needs it — and uses your '
        + 'own colours, spacing and guidelines instead of generic defaults.',
      explainerScope:
        'The connection is read-only, and covers published pages only. Drafts and hidden pages stay '
        + 'out of view.',
      step1: 'Step 1 — create a key',
      step1Desc:
        'Give the key a name that tells you later where it is used, for example the device or the tool.',
      nameLabel: 'Key name',
      namePlaceholder: 'E.g. Cursor on my laptop',
      create: 'Create key',
      created: 'Key created',
      createdBody: 'Copy the key now — it cannot be retrieved later.',
      createFailed: 'Could not create key',
      createFailedBody: 'The key could not be created. Please try again later.',
      copyNow: 'Copy this key now',
      copyNowDesc:
        'This is the only time you will see it. If you lose it, revoke it and create a new one.',
      tokenPlaceholder: 'YOUR-KEY',
      step2: 'Step 2 — add it to your AI tool',
      step2Desc: 'Pick the form that matches your tool below and paste it across.',
      addressLabel: 'Address',
      claudeCode: 'Claude Code',
      otherTools: 'Cursor and VS Code',
      otherToolsHint:
        'Paste this into your tool\'s MCP configuration file. If the key is not shown, it has since '
        + 'been hidden — create a new one.',
      simple: 'Connect',
      simpleDesc:
        'Paste this address into your AI tool. You then sign in with your own account and give '
        + 'permission — that is all it takes.',
      simpleHint:
        'In Claude Desktop: Settings → Connectors → "Add custom connector". In Claude Code: '
        + 'claude mcp add --transport http pentrical, followed by the address above.',
      connected: 'Connected AI tools',
      showKeys: 'Prefer to use a key?',
      hideKeys: 'Hide keys',
      keysWhen:
        'Only needed for tools that cannot sign in. For most tools the address above is enough.',
      noEnvironment: 'Open your documentation environment first',
      noEnvironmentDesc:
        'You are on Pentrical\'s general address, which holds no documentation. Go to the environment '
        + 'you want to connect — the right address is waiting for you there.',
      desktop: 'Claude Desktop',
      desktopWhy:
        'Note: entering only the address under "Add custom connector" will not work. Claude Desktop '
        + 'then looks for a sign-in service, while this connection uses a key. Pick one of these two '
        + 'instead.',
      desktopOption1: '1. Via the connector dialog (if you see "Request headers")',
      desktopOption1Desc:
        'Enter the address, open "Request headers", pick "authorization" and paste this value — '
        + 'including the word Bearer.',
      desktopOption2: '2. Via the configuration file (always works)',
      desktopOption2Desc:
        'No "Request headers" option? Paste this into claude_desktop_config.json and restart Claude '
        + 'Desktop. Node.js must be installed.',
      copy: 'Copy',
      copied: 'Copied',
      copyFailed: 'Copy failed',
      copyFailedBody: 'Your browser did not grant clipboard access.',
      existing: 'Existing keys',
      noneYet: 'You have not created any keys yet.',
      lastUsed: 'Last used: {date}',
      never: 'not used yet',
      revoke: 'Revoke',
      revokeConfirm:
        'Revoke key "{name}"? Any AI tool using this key loses access immediately.',
      revoked: 'Key revoked',
      revokedBody: '"{name}" no longer works.',
      revokeFailed: 'Could not revoke key',
      revokeFailedBody: 'The key could not be revoked. Please try again later.',
      loadError: 'Could not load the AI connection.',
    },
    theme: {
      title: 'Theme',
      subtitle: 'Decide how the site looks for all visitors.',
      invalidColor: 'Invalid colour',
      invalidColorBody: 'Use the format #rrggbb, for example #1a73e8.',
      saved: 'Theme saved',
      savedBody: 'The look & feel is immediately active for all visitors.',
      saveFailed: 'Saving failed',
      save: 'Save theme',
      resetAll: 'Reset everything to default',
      unsaved: 'Unsaved changes',
      groupSans: 'Sans-serif',
      groupSerif: 'Serif',
      groupMono: 'Monospace',
      logo: {
        heading: 'Logo',
        hint: 'Shown top-left in the header. Maximum height is 40 pixels.',
        typeLegend: 'Logo type',
        text: 'Text',
        textDesc: 'The site name as a text logo.',
        textLabel: 'Logo text',
        image: 'Image',
        imageDesc: 'Upload a JPG, PNG or WebP file (max. 2 MB).',
        currentAlt: 'Current logo',
        uploading: 'Uploading…',
        replace: 'Replace logo',
        upload: 'Upload logo',
        saved: 'Logo saved',
        settingSaved: 'Logo setting saved',
        textSaved: 'Logo text saved',
        uploadFailed: 'Upload failed',
        remove: 'Remove logo',
        removing: 'Removing…',
        removeHint: 'Removing the logo puts the site name back in the header as text.',
        confirmRemove: 'Remove the logo? The header will show the site name as text again.',
        removed: 'Logo removed',
        removeFailed: 'Could not remove the logo',
      },
      font: {
        heading: 'Font',
        hint: 'The base font for all text on the site, loaded via privacy-friendly Bunny.net.',
        baseLabel: 'Base font',
        default: 'Default (Source Sans 3)',
        preview: 'This is what body text looks like in this font — abcdefg ABCDEFG 0123456789.',
      },
      headings: {
        heading: 'Headings',
        hint: 'Set the font, size, line height and colour per heading level. Empty fields follow the default.',
        fontLabel: 'Font',
        baseOption: 'Base font',
        sizeLabel: 'Size (px)',
        sizeOf: 'Size of {label}',
        lineHeightLabel: 'Line height (\u00d7)',
        lineHeightHint: 'Times the font size: 1.4 on a 48px heading gives about 67px.',
        lineHeightOf: 'Line height of {label}',
        colorLabel: 'Colour',
        defaultPlaceholder: 'default',
        breakpointLegend: 'Screen width',
        desktop: 'Desktop',
        mobile: 'Mobile',
        desktopHint: 'Applies from 640 pixels wide and up.',
        mobileHint: 'Applies below 640 pixels. Leave empty to use the desktop value.',
        hasMobile: 'mobile set',
        heroTitle: 'Hero heading',
        heroTitleHint:
          'The heading in a hero section. Leave empty and it follows H1 - fill it in to make it bigger or tighter in that full-width band.',
        heroTitleFontNote:
          'Font follows H1; the colour is set per hero section, matched to its own background.',
        followsH1: 'follows H1',
        h1: 'Heading 1 (page title)',
        h2: 'Heading 2 (section title)',
        h3: 'Heading 3',
        h4: 'Heading 4',
        colorOf: 'Colour of {label}',
        hexOf: 'Hex colour code of {label}',
        resetColorOf: 'Reset colour of {label} to default',
      },
      colors: {
        heading: 'Colours',
        hint: 'Site-wide colours. Empty fields follow the default styling.',
        defaultPlaceholder: 'default',
        bgLabel: 'Page background colour',
        bgHint: 'The background behind all page content.',
        borderLabel: 'Border colour',
        borderHint: 'Borders of sections, columns and cards.',
        buttonLabel: 'Button colour',
        buttonHint: 'Background colour of primary buttons.',
        hexFor: 'Hex colour code for {label}',
        resetFor: 'Reset {label} to default',
      },
      appearance: {
        heading: 'Appearance',
        hint: 'Choose rounded or square corners for sections, columns, rows, cards and images, and set the radius. An image inside a row with a border or background follows the row.',
        sections: 'sections',
        columns: 'columns',
        cards: 'cards',
        rows: 'rows',
        images: 'images',
        roundedOf: 'Rounded {label}',
        radiusLabel: 'Radius',
      },
      footer: {
        heading: 'Footer',
        hint: 'Your own content at the bottom of every page, for all visitors.',
        showLabel: 'Show footer',
        hiddenWarning: 'The footer is hidden, so any content you put in it (for example a link to your accessibility statement) is no longer visible anywhere. The statement itself stays reachable at /accessibility — consider linking it from your menu.',
        contentLabel: 'Footer content',
        contentHint: 'Leave empty to show no footer. Want a link to your accessibility statement (e.g. to /accessibility)? Add it here yourself.',
        bgLabel: 'Footer background colour',
        bgHint: 'The background behind the whole footer.',
        textLabel: 'Footer text colour',
        textHint: 'Leave empty to derive a readable colour from the background.',
        autoPlaceholder: 'automatic',
        textAutoReset: 'Back to automatic text colour',
        readability: 'Readability',
        sample: 'This is how the footer text will look.',
        contrastOk: 'Contrast {ratio}:1 — meets WCAG AA for normal text.',
        contrastLow: 'Contrast {ratio}:1 — below the WCAG AA minimum of 4.5:1. This text may be hard to read.',
      },
      preview: {
        heading: 'Preview',
        hint: 'This is how the chosen settings come together on a page.',
        h1: 'Heading 1 — page title',
        h2: 'Heading 2 — section title',
        h3: 'Heading 3 — subheading',
        h4: 'Heading 4 — sub-subheading',
        body: 'This is a paragraph of body text in the base font, so you can judge the combination with the headings.',
        button: 'Example button',
      },
    },
  },

  search: {
    open: 'Search',
    shortcut: '⌘K',
    label: 'Search the documentation',
    placeholder: 'Search the documentation…',
    hint: 'Type to search titles and content',
    resultsLabel: 'Search results',
    noResults: 'No results for "{query}"',
    toOpen: 'to open',
    toNavigate: '↑↓ to select',
  },

  componentStatus: {
    in_development: 'In development',
    beta: 'Beta',
    stable: 'Stable',
    deprecated: 'Deprecated',
  },

  mcpConnectBlock: {
    defaultTitle: 'Connect your AI tool to this design system',
    defaultIntro:
      'Let your AI assistant read the guidelines, tokens and component specifications directly, so it uses the agreed values instead of inventing them.',
    copy: 'Copy',
    copied: 'Copied',
    copyLabel: 'Copy connection address',
    step1: '1. Open your AI tool\'s settings and add an MCP server.',
    step2: '2. Paste the address above and sign in with your account for this environment.',
    step3: '3. Then just ask your assistant about a component or a colour.',
    readOnly: 'The connection is read-only: an AI tool cannot change or publish anything.',
  },

  storybookBlock: {
    notConnected: 'No Storybook connected yet',
    notConnectedHint:
      'An administrator sets the address of your published Storybook once, under Settings → Storybook.',
    noStory: 'No story selected yet',
    noStoryHint:
      'Open the story in your Storybook and paste its address in the field on the left.',
    openInStorybook: 'Open in Storybook',
    frameTitle: 'Storybook story {story}',
  },

  tokens: {
    title: 'Design tokens',
    available: '{count} tokens available',
    refresh: 'Refresh',
    export: 'Export',
    searchPlaceholder: 'Search tokens...',
    searchLabel: 'Search design tokens',
    loading: 'Loading tokens...',
    emptyFound: 'No tokens found',
    emptyNone: 'No design tokens yet',
    emptyFoundHint: 'Try a different search term',
    emptyNoneHint: 'Tokens come from the colour and text styles in your Figma files',
    emptyToLibrary: 'To the Figma library',
    sources: 'Sources ({count}/{total})',
    sourcesTitle: 'Where do the tokens come from?',
    sourcesIntro:
      'Choose which Figma files supply their colour and text styles as tokens. A prototype or a one-off campaign is usually best left out: those styles are not part of the design system.',
    sourcesStyles: '{colors} colour styles, {texts} text styles',
    sourcesToggleLabel: 'Use tokens from {name}',
    sourcesCancel: 'Cancel',
    sourcesSave: 'Save',
    sourcesSaved: 'Sources updated',
    sourcesSavedBody: 'The list now holds {count} tokens from Figma',
    sourcesFailed: 'Could not save',
    sourcesFailedBody: 'Failed to update the token sources',
    filterSourceLabel: 'Filter by source',
    filterAll: 'All sources',
    filterFigma: 'From Figma',
    filterManual: 'Manual',
    filterCount: '{count} tokens',
    add: 'Add token',
    addTitle: 'New token',
    editTitle: 'Edit token',
    figmaHint:
      'This token comes from a Figma style. You can change its name and CSS variable — those belong to your code. The value follows the style and is refreshed on every sync.',
    fieldCategory: 'Category',
    fieldName: 'Name',
    fieldNameHint: 'Dots create groups: spacing.md becomes spacing → md in the JSON export.',
    fieldValue: 'Value',
    fieldCss: 'CSS variable',
    editLabel: 'Edit {name}',
    deleteLabel: 'Delete {name}',
    activateLabel: 'Enable {name}',
    deactivateLabel: 'Disable {name}',
    created: 'Token added',
    updated: 'Token updated',
    deleted: 'Token deleted',
    confirmDelete: 'Are you sure you want to delete "{name}"?',
    saveFailed: 'Could not save',
    saveFailedBody: 'Failed to save the token',
    deleteFailed: 'Could not delete',
    deleteFailedBody: 'Failed to delete the token',
    emptyCategory: 'No {category} tokens found',
    colorPreview: 'Colour preview: {value}',
    copyLabel: 'Copy {name}',
    inactive: 'Inactive',
    loadFailed: 'Loading failed',
    loadFailedBody: 'Could not load design tokens',
    exportSuccess: 'Export successful',
    exportSuccessBody: 'Tokens exported as {format}',
    exportFailed: 'Export failed',
    exportFailedBody: 'Could not export tokens',
    copied: 'Copied',
    copiedBody: '{label} copied to clipboard',
    copyFailed: 'Copy failed',
    copyFailedBody: 'Could not copy the value',
    categories: {
      color: 'Colours',
      typography: 'Typography',
      spacing: 'Spacing',
      sizing: 'Sizing',
      shadow: 'Shadows',
      border: 'Borders',
    },
  },

  figma: {
    // Terugvalmeldingen wanneer de API zelf geen melding meestuurt.
    errors: {
      connectFailed: 'Could not connect to Figma',
      disconnectFailed: 'Could not disconnect',
      filesFailed: 'Could not load the files',
      importFailed: 'Could not import the file',
      fileFailed: 'Could not load the file',
      syncFailed: 'Sync failed',
      deleteFailed: 'Could not delete the file',
      specsFailed: 'Could not load the specifications',
      codeFailed: 'Could not load the code',
      codeGenerateFailed: 'Could not generate the code',
    },
    common: {
      tryAgainLater: 'Please try again later',
      cancel: 'Cancel',
      never: 'Never',
      copied: 'Copied',
      copiedBody: '{label} copied to clipboard',
      copyFailed: 'Copy failed',
      copyValueFailedBody: 'Could not copy the value',
    },
    library: {
      title: 'Figma library',
      subtitle: 'Manage and sync your Figma design files',
      connected: 'Connected to Figma',
      notConnected: 'Not connected',
      import: 'Import file',
      connect: 'Connect with Figma',
      reconnect: 'Reconnect',
      reconnectHint: 'Log in with Figma again — use this if syncing fails with an authorisation error',
      disconnect: 'Disconnect',
      disconnectHint: 'Stop updating from Figma. Your files and documentation stay as they are',
      confirmDisconnect: 'Disconnect Figma from this workspace?\n\nYour imported files and documentation stay exactly as they are. Only updating from Figma stops until you connect again.',
      disconnected: 'Figma disconnected',
      disconnectedBody: 'Your files and documentation are untouched. Connect again whenever you want to resume updating.',
      searchPlaceholder: 'Search files...',
      searchLabel: 'Search Figma files',
      loading: 'Loading files...',
      emptyFound: 'No files found',
      emptyNone: 'No files yet',
      emptyFoundHint: 'Try a different search term',
      emptyNoneHint: 'Import your first Figma file to get started',
      emptyNotConnectedHint: 'Connect with Figma first; after that you import your design files here',
      lostTitle: 'The Figma connection needs renewing',
      lostBody: 'Your documentation stays visible as it is. Only automatic updating from Figma has stopped until you connect again.',
      lostCause: 'Usually this happens because the same Figma account was connected elsewhere, or because access was revoked in Figma.',
      listLabel: 'Figma files',
      thumbnailAlt: 'Thumbnail of {name}',
      syncedAt: 'Synced: {date}',
      syncLabel: 'Sync {name}',
      sync: 'Sync',
      openInFigma: 'Open {name} in Figma (opens in a new tab)',
      deleteLabel: 'Delete {name}',
      urlRequired: 'Figma URL is required',
      urlInvalid: 'Enter a valid Figma URL',
      importStarted: 'Import started',
      importStartedBody: '{name} is being synced in the background',
      importFailed: 'Could not import file',
      alreadyImported: 'This Figma file is already in this hub.',
      inOtherHub: 'This Figma file is already in the hub "{hub}". A Figma file can be in one hub at a time, so you cannot add it here as well. Remove it from "{hub}" first if you want to use it in this hub.',
      inOtherHubUnnamed: 'This Figma file is already in use in another hub in this account. A Figma file can be in one hub at a time, so you cannot add it here as well. Ask the account owner to remove it there first.',
      withoutHub: 'This Figma file is still registered in this account, but not in any hub. That should not happen. Please contact us and we will clear it up for you.',
      syncStarted: 'Synchronisation started',
      syncStartedBody: 'The file is being synced in the background',
      syncFailed: 'Synchronisation failed',
      confirmDelete: 'Are you sure you want to delete "{name}"? This cannot be undone.',
      deleted: 'File deleted',
      deletedBody: '{name} was successfully deleted',
      deleteFailed: 'Delete failed',
      modalTitle: 'Import Figma file',
      urlLabel: 'Figma URL',
      urlHint: 'Paste the URL of your Figma file here',
      nameLabel: 'Name (optional)',
      nameHint: 'Leave empty to use the name from Figma',
      namePlaceholder: 'Design System Components',
      importing: 'Importing...',
      importAction: 'Import',
    },
    file: {
      loadError: 'File not found',
      loadErrorBody: 'Could not load the Figma file',
      back: 'Back',
      nodes: 'nodes',
      viewer: 'Interactive viewer',
      sync: 'Synchronise',
      openInFigma: 'Open in Figma',
      searchPlaceholder: 'Search nodes...',
      searchLabel: 'Search nodes',
      gridView: 'Grid view',
      listView: 'List view',
      loading: 'Loading...',
      emptyTitle: 'No nodes found',
      emptyHintSearch: 'Try a different search term',
      emptyHintNone: 'This file has no nodes yet',
      previewAlt: 'Preview of {name}',
      colName: 'Name',
      colType: 'Type',
      colDimensions: 'Dimensions',
      colActions: 'Actions',
      specsLabel: 'Design specifications',
      types: {
        component: 'Components',
        frame: 'Frames',
        group: 'Groups',
        text: 'Text',
        other: 'Other',
      },
    },
    syncPopover: {
      title: 'Syncing with Figma',
      fetching: 'Fetching the file from Figma…',
      processing: 'Processing layers: {done} of {total}',
      processingShort: 'Processing layers…',
      finishing: 'Updating styles, tokens and previews…',
      explainer: 'Pentrical fetches the complete file and computes the specs for every layer. Large files can take a minute - you can keep working in the meantime.',
      dismiss: 'Hide progress message',
      doneTitle: 'Sync complete',
      doneBody: '{name} has been updated ({count} layers).',
      failedTitle: 'Sync failed',
      failedBody: '{name} could not be updated. Please try again later.',
    },
    viewer: {
      chooseScreen: 'Browse your library',
      chooseScreenHint: 'Select a screen to view its elements and specifications.',
      screenSelectLabel: 'Choose screen',
      page: 'Page',
      layers: 'Layers',
      structureOf: 'Structure of {name}',
      specs: 'Specs',
      viewLabel: 'View',
      gridView: 'Grid view with previews',
      grid: 'Grid',
      listView: 'List view',
      list: 'List',
      searchPlaceholder: 'Search a screen...',
      searchLabel: 'Search a screen',
      syncing: 'Synchronisation in progress — the list fills automatically.',
      previewsSkipped:
        'For this large file ({count} screens) previews are not fetched in advance. Open a screen and the preview loads immediately.',
      previewsLoading:
        'Fetching previews ({done} of {total}) — screens without a preview still work; the detail view loads the preview itself.',
      syncingTitle: 'This file is being synchronised',
      syncingBody:
        'Screens appear here automatically once they arrive. For large files this can take a few minutes.',
      noScreens: 'No screens found. Synchronise the file first.',
      noSearchResults: 'No screens found for "{query}".',
      screenSingular: 'screen',
      screenPlural: 'screens',
      loadingScreen: 'Loading screen...',
      clickElement: 'Click an element in the design to view its specifications.',
      frameLoadError: 'Screen not loaded',
      frameLoadErrorBody: 'Could not load the contents of this screen',
      interiorsFailed: 'Component details not loaded',
      interiorsFailedBody: 'The layers inside components cannot be fetched from Figma right now. Clicking selects the component itself.',
    },
    specs: {
      close: 'Close panel',
      tabSpecs: 'Specifications',
      tabCode: 'Code',
      noSpecs: 'No specifications available',
      colorPreview: 'Colour preview: {value}',
      copyLabel: 'Copy {label}',
      categories: {
        dimensions: 'Dimensions',
        colors: 'Colours and borders',
        typography: 'Typography',
        spacing: 'Spacing',
        effects: 'Effects',
        layout: 'Layout',
      },
    },
    code: {
      copied: 'Code copied',
      copiedBody: '{format} code copied to clipboard',
      copyFailed: 'Copy failed',
      copyFailedBody: 'Could not copy the code',
      generated: 'Code generated',
      generatedBody: 'The code has been regenerated',
      generateFailed: 'Generation failed',
      generateFailedBody: 'Could not generate the code',
      loading: 'Loading code...',
      noCode: 'No code available for this format',
      generate: 'Generate code',
      regenerateLabel: 'Regenerate {format} code',
      copyCodeLabel: 'Copy {format} code',
    },
    canvas: {
      label:
        'Canvas of {name}. Click an element for specifications, or use the arrow keys to navigate the structure. Escape clears the selection.',
      zoom: 'Zoom',
      zoomOut: 'Zoom out',
      zoomIn: 'Zoom in',
      zoomFit: 'Zoom to fit',
      fit: 'Fit',
      actualSize: 'Actual size (100%)',
      previewAlt: 'Preview of {name}',
      previewLoading: 'Fetching the preview from Figma…',
      previewLoadingHint: 'Figma renders the screen live - large screens can take a moment.',
      previewUnavailable: 'No preview available for this screen',
    },
    picker: {
      libraryLoadError: 'Could not load the Figma library.',
      framesLoadError: 'Could not load the screens of this file.',
      backToFiles: 'Back to file list',
      title: 'Choose a Figma design',
      close: 'Close',
      searchPlaceholder: 'Search a screen by name…',
      loadingLibrary: 'Loading library…',
      noSyncedFiles: 'No synchronised Figma files found. Import a file in the library first.',
      pageCount: '{count} pages',
      figmaFile: 'Figma file',
      loadingScreens: 'Loading screens…',
      noSearchResults: 'No screens found for this search.',
      noScreens: 'This file has no screens.',
    },
  },

  textStyles: {
    defaultSample: 'The quick brown fox jumps over the lazy dog',
    fontUnavailable:
      'Preview uses a substitute font: {fonts} is not available in the browser. The specifications below are the real values from Figma.',
    spec: {
      family: 'Family',
      size: 'Size',
      lineHeight: 'Line height',
      letterSpacing: 'Letter spacing',
      transform: 'Text case',
      decoration: 'Decoration',
      paragraphSpacing: 'Paragraph spacing',
    },
    picker: {
      title: 'Choose text styles',
      loading: 'Loading text styles…',
      loadError: 'Could not load the text styles of this file.',
      searchPlaceholder: 'Search a style by name…',
      noStyles:
        'No text styles found in this file. Synchronise the file again — styles are collected during a sync.',
      ungrouped: 'Other',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      selectedCount: '{count} selected',
      confirm: 'Add styles',
      previewWord: 'Abc',
    },
  },

  colorStyles: {
    picker: {
      title: 'Choose colour style',
      loading: 'Loading colour styles…',
      loadError: 'Could not load the colour styles of this file.',
      searchPlaceholder: 'Search a style by name…',
      noStyles:
        'No colour styles found in this file. Synchronise the file again — styles are collected during a sync.',
      ungrouped: 'Other',
    },
  },

  pages: {
    status: {
      published: 'Published',
      draft: 'Draft',
      archived: 'Archived',
    },
    // Terugvalmeldingen wanneer de API zelf geen melding meestuurt.
    errors: {
      fetchListFailed: 'Could not load the pages',
      fetchFailed: 'Could not load the page',
      homepageFailed: 'Could not load the home page',
      notFound: 'Page not found',
      createFailed: 'Could not create the page',
      updateFailed: 'Could not update the page',
      deleteFailed: 'Could not delete the page',
      publishFailed: 'Could not publish the page',
      unpublishFailed: 'Could not unpublish the page',
      duplicateFailed: 'Could not duplicate the page',
    },
    noSection: 'No section',
    unknownSection: 'Unknown section',
    tryAgainLater: 'Please try again later',
    cancel: 'Cancel',
    list: {
      title: 'Pages',
      stats: '{total} pages • {published} published • {draft} drafts',
      newPage: 'New page',
      searchPlaceholder: 'Search pages...',
      searchLabel: 'Search pages',
      filterStatus: 'Filter by status',
      allStatuses: 'All statuses',
      filterSection: 'Filter by section',
      allSections: 'All sections',
      loading: 'Loading pages...',
      emptyFiltered: 'No pages found',
      emptyNone: 'No pages yet',
      emptyFilteredHint: 'Try different filters',
      emptyNoneHint: 'Create your first page to get started',
      colTitle: 'Title',
      colSection: 'Section',
      colStatus: 'Status',
      colUpdated: 'Last modified',
      colActions: 'Actions',
      actionsFor: 'Actions for {title}',
      edit: 'Edit',
      view: 'View',
      publish: 'Publish',
      unpublish: 'Unpublish',
      duplicate: 'Duplicate',
      delete: 'Delete',
      modalTitle: 'Create new page',
      titleLabel: 'Title',
      titlePlaceholder: 'E.g. Buttons',
      sectionLabel: 'Section',
      creating: 'Creating...',
      create: 'Create',
      titleRequired: 'Title is required',
      created: 'Page created',
      createdBody: '{title} was successfully created',
      createFailed: 'Creation failed',
      published: 'Page published',
      publishedBody: '{title} is now published',
      publishFailed: 'Publishing failed',
      unpublished: 'Page unpublished',
      unpublishedBody: '{title} is now a draft',
      unpublishFailed: 'Unpublishing failed',
      duplicated: 'Page duplicated',
      duplicatedBody: 'Copy of {title} created',
      duplicateFailed: 'Duplication failed',
      confirmDelete: 'Are you sure you want to delete "{title}"? This cannot be undone.',
      deleted: 'Page deleted',
      deletedBody: '{title} was successfully deleted',
      deleteFailed: 'Delete failed',
    },
    view: {
      learnMore: 'Learn more',
      loading: 'Loading page...',
      edit: 'Edit',
      noHomepageTitle: 'No homepage set yet',
      noHomepageHintPre: 'Open a page in the editor and turn on the',
      homepageToggleName: 'Set as homepage',
      noHomepageHintPost: 'toggle there to decide what visitors see here.',
      noHomepageVisitor: 'No home page is available yet.',
      notFoundTitle: 'Page not found',
      notFoundBody: 'The requested page does not exist or has been removed.',
      backToHome: 'Back to home',
      breadcrumb: 'Breadcrumb',
      home: 'Home',
      lastUpdated: 'Last updated: {date}',
      openImage: 'Open image',
      viewSpecs: 'View specs of {name} in the interactive viewer',
      figmaUnavailable: 'The embedded Figma design "{name}" is no longer available.',
      unknownBlock: 'Unknown block type: {type}',
      empty: 'This page has no content yet.',
      imageUnavailable: 'Image unavailable',
      copyHexLabel: 'Copy hex value {hex}',
      copyRgbLabel: 'Copy rgb value {rgb}',
      copied: 'Copied',
      copiedBody: '{label} copied to clipboard',
      copyFailed: 'Copy failed',
      copyFailedBody: 'Could not copy the value',
    },
  },
}
